import {
  findEnclosingBalancedDiv,
  isMultiSelectComplete,
  multiSelectBounds,
  renderMultiSelectPdfHtml,
} from '@besty/shared/rentalAgreements/multiSelect';
import {
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_ACCEPT,
  CAPPED_GUEST_INFO_MODE,
  ExtractedFormComponent,
  FormUploadRecord,
  GuestInfoMode,
  clampGuestInfoCount,
  findIncompleteFormComponents,
  getFormComponentDescriptor,
  guestInfoCeiling,
  isGuestAddableMode,
} from '@besty/shared/formComponents';
import { loadStripe } from '@stripe/stripe-js';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import Banner from '@/components/feedback/Banner';
import { LanguageSelector } from '@/components/LanguageSelector';
import Layout from '@/components/Layout';
import { PortalOutletContext } from '@/components/portal/PortalShellContainer';
import { Button } from '@/components/ui';
import { useAuth } from '@/contexts';
import { useLanguage } from '@/contexts/LanguageContext';
import { apiClient } from '@/lib/api';
import {
  FormUploadUiState,
  mountFormComponentAddress,
  mountFormComponentAttachment,
  mountFormComponentTextInput,
} from '@/components/formComponentMounts';

/** Check if a file is an image — handles HEIC where browser may report empty mimetype */
const isImageFile = (file: File): boolean => {
  if (file.type.startsWith('image/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
};

// Note: Session data now loaded from API, localStorage only used for progress backup

interface PageData {
  id: string;
  name: string;
  html: string;
  css: string;
}

interface ImageUpload {
  id: string;
  title: string;
  required: boolean;
}

interface FieldWithRequired {
  id: string;
  required: boolean;
}

interface TypedSignatureField extends FieldWithRequired {
  populateGuestName?: boolean;
}

interface InputField extends FieldWithRequired {
  placeholder?: string;
}

interface CheckboxField {
  id: string;
  label: string;
  // Untranslated (template source language) label, used for the signed PDF so the
  // legal document stays in one consistent language even when the guest UI is translated.
  originalLabel?: string;
  required: boolean;
}

interface NumberInputField {
  id: string;
  label: string;
  originalLabel?: string;
  min: string;
  max: string;
  required: boolean;
}

interface MultiSelectField {
  id: string;
  label: string;
  // Untranslated (template source language) label + options, used for the signed PDF
  // so the legal document stays in one consistent language even when the guest UI is translated.
  originalLabel?: string;
  options: string[];
  originalOptions?: string[];
  required: boolean;
  multiple: boolean;
  min: string;
  max: string;
}

interface GuestInfoComponent {
  id: string;
  mode: GuestInfoMode;
  label: string;
  fields: Array<{ type: string; label?: string; required: boolean }>;
  guestCount: number;
  /** Capped mode only: listing max occupancy minus the primary signer. */
  maxGuestCount?: number | null;
}

interface SigningSession {
  pages: PageData[];
  requiredFieldIds: string[]; // Backward compatibility
  signatureIds: string[];
  initialIds: string[];
  inputIds: string[];
  signatureFields?: FieldWithRequired[];
  initialFields?: FieldWithRequired[];
  inputFields?: InputField[];
  typedSignatureFields?: TypedSignatureField[];
  checkboxes?: CheckboxField[];
  numberInputs?: NumberInputField[];
  multiSelects?: MultiSelectField[];
  selfieRequired: boolean;
  idRequired: boolean;
  imageUploads: ImageUpload[];
  customImages: Record<string, string>;
  // BES-9331: registry form components (phone, email, attachment, address)
  // with server-translated labels; originalLabel stays source-language for PDF
  formComponents?: ExtractedFormComponent[];
  status: 'active' | 'completed' | 'expired';
  templateName?: string;
  guestInfoComponents?: GuestInfoComponent[];
  guestName?: string;
}

// GrapesJS HTML-encodes attribute values (e.g. & → &amp;); decode before re-escaping
const decodeHtml = (str: string): string =>
  str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&');

// Walk each page's HTML and map every field/component id → its page index.
// Used so the multi-page hint banner can point users back to the page that has an unfilled required field.
const FIELD_ID_ATTRS = [
  'data-signature-id',
  'data-typed-signature-id',
  'data-initials-id',
  'data-input-id',
  'data-checkbox-id',
  'data-number-id',
  'data-multiselect-id',
  'data-image-id',
  'data-guest-info-id',
  'data-form-component-id',
];
interface PageLocationIndex {
  fieldIdToPage: Map<string, number>;
  fieldIdToLabel: Map<string, string>;
  selfiePage: number | null;
  idPage: number | null;
}
const ATTR_TO_LABEL: Record<string, string> = {
  'data-signature-id': 'Signature',
  'data-typed-signature-id': 'Typed Signature',
  'data-initials-id': 'Initials',
  'data-checkbox-id': 'Checkbox',
  'data-number-id': 'Number',
  'data-multiselect-id': 'Multi-select',
  'data-image-id': 'Photo Upload',
  'data-guest-info-id': 'Guest Information',
};
const buildPageLocationIndex = (pages: PageData[]): PageLocationIndex => {
  const fieldIdToPage = new Map<string, number>();
  const fieldIdToLabel = new Map<string, string>();
  let selfiePage: number | null = null;
  let idPage: number | null = null;
  const parser = new DOMParser();
  pages.forEach((page, pageIdx) => {
    const doc = parser.parseFromString(page.html || '', 'text/html');
    const selector = FIELD_ID_ATTRS.map((a) => `[${a}]`).join(',');
    doc.querySelectorAll(selector).forEach((el) => {
      for (const attr of FIELD_ID_ATTRS) {
        const id = el.getAttribute(attr);
        if (id && !fieldIdToPage.has(id)) {
          fieldIdToPage.set(id, pageIdx);
          if (attr === 'data-input-id') {
            const label = el.getAttribute('data-label');
            if (label) {
              fieldIdToLabel.set(id, label);
            } else {
              const inp = el.querySelector('input, textarea');
              fieldIdToLabel.set(id, inp?.getAttribute('type') === 'date' ? 'Date' : 'Text Field');
            }
          } else if (ATTR_TO_LABEL[attr]) {
            fieldIdToLabel.set(id, ATTR_TO_LABEL[attr]);
          }
        }
      }
    });
    if (selfiePage === null && doc.querySelector('[data-selfie-id]')) selfiePage = pageIdx;
    if (idPage === null && doc.querySelector('[data-id-id]')) idPage = pageIdx;
  });
  return { fieldIdToPage, fieldIdToLabel, selfiePage, idPage };
};

// HTML escape utility to prevent XSS when interpolating user values into HTML strings
const escapeHtml = (str: string | number | undefined | null): string => {
  if (str === undefined || str === null) return '';
  const s = String(str);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const SignAgreement: React.FC = () => {
  useAuth(); // Ensure auth context is available
  const portalOutlet = useOutletContext<PortalOutletContext | null>();
  const refetchPortal = portalOutlet?.refetch;
  const { language, setLanguage, isLanguageFromStorage } = useLanguage();
  // Capture in a ref so the loadSession effect can read the latest value
  // without having to depend on it (would cause refetch loops on every pick).
  const isLanguageFromStorageRef = useRef(isLanguageFromStorage);
  useEffect(() => {
    isLanguageFromStorageRef.current = isLanguageFromStorage;
  }, [isLanguageFromStorage]);
  const { t } = useTranslation('portal');

  // Built-in field-type descriptors shown in the "earlier pages need attention"
  // banner/nav pills. Literal t() calls keep the keys statically discoverable.
  // Host-authored labels (not in this map) are left as-is; they are translated server-side.
  const localizeFieldLabel = (label: string): string => {
    const known: Record<string, string> = {
      Signature: t('Signature'),
      'Typed Signature': t('Typed Signature'),
      Initials: t('Initials'),
      Checkbox: t('Checkbox'),
      Number: t('Number'),
      'Photo Upload': t('Photo Upload'),
      'Guest Information': t('Guest Information'),
      Selfie: t('Selfie'),
      'ID Upload': t('ID Upload'),
      'Text Field': t('Text Field'),
      Date: t('Date'),
      Email: t('Email'),
      Phone: t('Phone'),
      'Required Field': t('Required Field'),
    };
    return known[label] ?? label;
  };
  const [session, setSession] = useState<SigningSession | null>(null);
  // Store original template pages for PDF generation (in template's source language)
  const originalPagesRef = useRef<PageData[] | null>(null);
  // Map field/component id → page index it lives on (built from page HTML at load)
  const pageLocationRef = useRef<PageLocationIndex>({
    fieldIdToPage: new Map(),
    fieldIdToLabel: new Map(),
    selfiePage: null,
    idPage: null,
  });
  const templateLanguageRef = useRef<string>('en');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  // Furthest page user has advanced to (via Next); used to gate the "earlier pages have gaps" hint
  const [maxVisitedPageIndex, setMaxVisitedPageIndex] = useState(0);
  const [signatures, setSignatures] = useState<Record<string, string>>({});
  const [typedSignatures, setTypedSignatures] = useState<Record<string, string>>({});
  const [initials, setInitials] = useState<Record<string, string>>({});
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [sigDims, setSigDims] = useState<Record<string, { w: number; h: number }>>({});
  const [selfieStatus, setSelfieStatus] = useState<'pending' | 'completed' | 'failed'>('pending');
  const [selfieUrl, setSelfieUrl] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [selfieModalOpen, setSelfieModalOpen] = useState(false);
  const [initialsModalOpen, setInitialsModalOpen] = useState(false);
  const [initialsModalId, setInitialsModalId] = useState<string | null>(null);
  const [initialsModalValue, setInitialsModalValue] = useState('');
  const [selfieCaptureToken, setSelfieCaptureToken] = useState<string | null>(null);
  const [idStatus, setIdStatus] = useState<'pending' | 'completed' | 'failed'>('pending');
  const [idUrl, setIdUrl] = useState<string | null>(null);
  const [idFileName, setIdFileName] = useState<string | null>(null);
  const [idError, setIdError] = useState<string | null>(null);
  const idInputRef = useRef<HTMLInputElement>(null);
  const [customImages, setCustomImages] = useState<
    Record<
      string,
      {
        url: string | null;
        status: 'pending' | 'completed' | 'failed';
        fileName: string | null;
        error: string | null;
      }
    >
  >({});
  const customImageInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [customImageRefreshTrigger, setCustomImageRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const selfiePollRef = useRef<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [knownSigIds, setKnownSigIds] = useState<Set<string>>(new Set());
  const [knownInitIds, setKnownInitIds] = useState<Set<string>>(new Set());
  const [knownInputIds, setKnownInputIds] = useState<Set<string>>(new Set());
  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>({});
  const [numberValues, setNumberValues] = useState<Record<string, string>>({});
  const [multiSelectValues, setMultiSelectValues] = useState<Record<string, string[]>>({});

  // BES-9331: registry form component state. The ref mirrors the values state
  // so finalize/PDF code running inside a stale closure still reads the latest
  // saved values; the state drives re-renders of the completion gate.
  const [formComponentValues, setFormComponentValues] = useState<Record<string, string>>({});
  const formComponentValuesRef = useRef<Record<string, string>>({});
  const [formUploads, setFormUploads] = useState<Record<string, FormUploadUiState>>({});
  const formComponentFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const formComponentFlushersRef = useRef<Record<string, () => void | Promise<void>>>({});

  // Guest info state
  const [guestInfoValues, setGuestInfoValues] = useState<Record<string, string>>({});
  const [guestInfoCounts, setGuestInfoCounts] = useState<Record<string, number>>({});
  // Tracks which guest-info image ID a selfie capture is for (null = standard selfie)
  const pendingGuestSelfieRef = useRef<string | null>(null);

  // Stripe verification state
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationCompleted, setVerificationCompleted] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [minimumAge, setMinimumAge] = useState<number | null>(null);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const verificationPollRef = useRef<number | null>(null);
  const MAX_VERIFICATION_ATTEMPTS = 5;

  // set title — use template name when available (scoped server-side)
  useEffect(() => {
    document.title = session?.templateName || t('Rental Agreement');
  }, [session?.templateName, t]);

  // Load signing session (re-fetch when language changes for translation)
  useEffect(() => {
    let cancelled = false;
    let willRefetch = false;
    // Load session from API
    const loadSession = async () => {
      try {
        setLoading(true);
        setError(null);
        // Always fetch raw template first for PDF generation if not already stored
        let rawData: any | null = null;
        if (!originalPagesRef.current) {
          rawData = (await apiClient.get(`/agreements/session`)) as any;
          if (cancelled) return;
          originalPagesRef.current = rawData.pages;
          templateLanguageRef.current = rawData.templateLanguage || 'en';
        }

        // On first load, if the guest hasn't manually picked a language and the
        // template was authored in something other than the current UI language,
        // switch to the template's language. The effect will re-fire with the
        // new `language` and load the untranslated version directly.
        if (
          rawData?.templateLanguage &&
          !isLanguageFromStorageRef.current &&
          rawData.templateLanguage !== language
        ) {
          willRefetch = true;
          setLanguage(rawData.templateLanguage);
          return;
        }

        // Always fetch the display payload with the language param. The raw
        // no-language fetch above never triggers backend translation
        // (needsTranslation is gated on languageParam !== null), so a bundle
        // with a mismatched-language addendum returns the addendum
        // untranslated. The with-language fetch runs the per-page translate
        // loop, which short-circuits identically when source=target so the
        // common all-matching case is still cheap.
        const data = (await apiClient.get(`/agreements/session?language=${language}`)) as any;
        if (cancelled) return;

        const session: SigningSession = {
          pages: data.pages,
          requiredFieldIds: data.requiredFieldIds, // Backward compatibility
          signatureIds: data.signatureIds || [],
          initialIds: data.initialIds || [],
          inputIds: data.inputIds || [],
          signatureFields: data.signatureFields || [],
          initialFields: data.initialFields || [],
          inputFields: data.inputFields || [],
          typedSignatureFields: data.typedSignatureFields || [],
          checkboxes: data.checkboxes || [],
          numberInputs: data.numberInputs || [],
          multiSelects: data.multiSelects || [],
          selfieRequired: data.selfieRequired,
          idRequired: data.idRequired || false, // Will be set by backend if ID blocks exist
          imageUploads: data.imageUploads || [],
          customImages: data.customImages || {},
          formComponents: data.formComponents || [],
          status: data.status,
          templateName: data.templateName,
          guestInfoComponents: data.guestInfoComponents || [],
          guestName: data.guestName || '',
        };

        // Set selfie URL from session data if available
        if (data.selfieUrl) {
          setSelfieUrl(data.selfieUrl);
          setSelfieStatus('completed'); // ensure completion is recognized on reload
        }

        // Set ID URL from session data if available
        if (data.idImageUrl) {
          setIdUrl(data.idImageUrl);
          setIdStatus('completed');
        }

        // Initialize custom images state from session data
        if (data.customImages && typeof data.customImages === 'object') {
          const initialCustomImages: typeof customImages = {};
          Object.entries(data.customImages).forEach(([imageId, url]) => {
            initialCustomImages[imageId] = {
              url: url as string,
              status: 'completed',
              fileName: null,
              error: null,
            };
          });
          setCustomImages(initialCustomImages);
          // Trigger UI refresh to show previously uploaded images
          setCustomImageRefreshTrigger((prev) => prev + 1);
        }

        // BES-9331: initialize attachment uploads from progress_json.formUploads
        if (data.formUploads && typeof data.formUploads === 'object') {
          const initialFormUploads: Record<string, FormUploadUiState> = {};
          Object.entries(data.formUploads).forEach(([fieldId, record]: [string, any]) => {
            if (record?.url) {
              initialFormUploads[fieldId] = {
                record: record as FormUploadRecord,
                status: 'completed',
                fileName: record.fileName || null,
                error: null,
              };
            }
          });
          if (Object.keys(initialFormUploads).length > 0) setFormUploads(initialFormUploads);
        }

        // Set Stripe verification status
        setVerificationRequired(data.stripeVerificationRequired || false);
        setVerificationCompleted(data.stripeVerificationCompleted || false);
        setMinimumAge(data.minimumAge ?? null);
        setVerificationAttempts(data.stripeVerificationAttempts || 0);

        setSession(session);
        pageLocationRef.current = buildPageLocationIndex(session.pages);
        setCompleted(data.status === 'completed');
        if (data.pdfUrl) setPdfUrl(data.pdfUrl);
        console.log('📄 Loaded signing session:', session);

        // Load saved progress if any
        if (data.progress) {
          const savedSignatures: Record<string, string> = {};
          const savedTypedSignatures: Record<string, string> = {};
          const savedInitials: Record<string, string> = {};
          const savedInputs: Record<string, string> = {};
          const savedCheckboxes: Record<string, boolean> = {};
          const savedNumbers: Record<string, string> = {};
          const savedMultiSelects: Record<string, string[]> = {};
          const savedFormComponents: Record<string, string> = {};
          const savedGuestInfoValues: Record<string, string> = {};
          const savedGuestInfoCounts: Record<string, number> = {};

          Object.entries(data.progress).forEach(([fieldId, fieldData]: [string, any]) => {
            if (fieldData.fieldType === 'signature') {
              savedSignatures[fieldId] = fieldData.value;
            } else if (fieldData.fieldType === 'typed-signature') {
              savedTypedSignatures[fieldId] = fieldData.value;
            } else if (fieldData.fieldType === 'initials') {
              savedInitials[fieldId] = fieldData.value;
            } else if (fieldData.fieldType === 'input') {
              savedInputs[fieldId] = fieldData.value;
            } else if (fieldData.fieldType === 'checkbox') {
              savedCheckboxes[fieldId] = fieldData.value;
            } else if (fieldData.fieldType === 'number') {
              savedNumbers[fieldId] = fieldData.value;
            } else if (fieldData.fieldType === 'multi-select') {
              savedMultiSelects[fieldId] = Array.isArray(fieldData.value) ? fieldData.value : [];
            } else if (fieldData.fieldType === 'form-component') {
              savedFormComponents[fieldId] = typeof fieldData.value === 'string' ? fieldData.value : '';
            } else if (fieldData.fieldType === 'guest-info-meta') {
              // Guest count for manual mode components
              savedGuestInfoCounts[fieldId] = Number(fieldData.value) || 0;
            } else if (typeof fieldData.fieldType === 'string' && fieldData.fieldType.startsWith('guest-info-')) {
              const subType = fieldData.fieldType.replace('guest-info-', '');
              if (subType === 'signature') {
                // Legacy: merge into main signatures map
                savedSignatures[fieldId] = fieldData.value;
              } else if (subType === 'initials') {
                // Legacy: merge into main initials map
                savedInitials[fieldId] = fieldData.value;
              } else {
                savedGuestInfoValues[fieldId] = fieldData.value;
              }
            }
          });

          if (Object.keys(savedSignatures).length > 0) {
            setSignatures(savedSignatures);
            console.log('Loaded saved signatures:', Object.keys(savedSignatures));
          }

          if (Object.keys(savedTypedSignatures).length > 0) {
            setTypedSignatures(savedTypedSignatures);
          }

          if (Object.keys(savedInitials).length > 0) {
            setInitials(savedInitials);
            console.log('Loaded saved initials:', Object.keys(savedInitials));
          }

          if (Object.keys(savedCheckboxes).length > 0) {
            setCheckboxValues(savedCheckboxes);
            console.log('Loaded saved checkboxes:', Object.keys(savedCheckboxes));
          }

          if (Object.keys(savedNumbers).length > 0) {
            setNumberValues(savedNumbers);
            console.log('Loaded saved number inputs:', Object.keys(savedNumbers));
          }

          if (Object.keys(savedMultiSelects).length > 0) {
            setMultiSelectValues(savedMultiSelects);
            console.log('Loaded saved multi-selects:', Object.keys(savedMultiSelects));
          }

          if (Object.keys(savedFormComponents).length > 0) {
            formComponentValuesRef.current = { ...formComponentValuesRef.current, ...savedFormComponents };
            setFormComponentValues(savedFormComponents);
            console.log('Loaded saved form components:', Object.keys(savedFormComponents));
          }

          if (Object.keys(savedInputs).length > 0) {
            setInputs(savedInputs);
            console.log('Loaded saved inputs:', Object.keys(savedInputs));
          }

          if (Object.keys(savedGuestInfoValues).length > 0) {
            setGuestInfoValues(savedGuestInfoValues);
          }
          if (Object.keys(savedGuestInfoCounts).length > 0) {
            // The cap can shrink between visits when a host lowers the listing's
            // max occupancy. Clamp the saved counts on load so the count we
            // render is the count finalize receives, otherwise the guest sees
            // fewer cards than they submit and the server rejects it.
            const normalizedCounts: Record<string, number> = { ...savedGuestInfoCounts };
            const countWritebacks: Promise<unknown>[] = [];
            for (const comp of session.guestInfoComponents || []) {
              const saved = normalizedCounts[comp.id];
              if (typeof saved !== 'number') continue;
              const clamped = clampGuestInfoCount(saved, comp.mode, comp.maxGuestCount);
              if (clamped === saved) continue;
              normalizedCounts[comp.id] = clamped;
              if (data.status === 'completed') continue;
              // Write the lower count back to progress too. A completed session
              // loads uncapped, so without this a post-signing view rehydrates
              // the pre-clamp count and shows cards the signed PDF never had.
              countWritebacks.push(
                apiClient
                  .patch('/agreements/progress', {
                    fieldId: comp.id,
                    value: String(clamped),
                    fieldType: 'guest-info-meta',
                  })
                  .catch((err: unknown) => console.error('Failed to persist clamped guest count:', err))
              );
            }
            // Finish the write-back before the page becomes signable, so the
            // count in progress cannot land after finalization.
            if (countWritebacks.length > 0) await Promise.all(countWritebacks);
            setGuestInfoCounts(normalizedCounts);
          }
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load signing session:', e);
        setError(e instanceof Error ? e.message : 'Failed to load agreement');
      } finally {
        if (!cancelled && !willRefetch) setLoading(false);
      }
    };

    loadSession();
    return () => {
      cancelled = true;
    };
  }, [language]);

  // ID file picker handler
  const onIdPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.currentTarget; // capture early
    if (isCompletedSession) {
      inputEl.value = '';
      return;
    }
    const file = inputEl.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      setIdError('Unsupported file. Please upload a photo (JPG/PNG/HEIC).');
      setIdStatus('failed');
      inputEl.value = '';
      return;
    }
    setIdError(null);

    try {
      // Get capture token for type='id'
      const r = await apiClient.post(`/agreements/selfie/start`, {
        type: 'id',
      });
      const { captureToken } = r as any;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('token', captureToken);

      const up = await apiClient.post(`/agreements/selfie-upload`, fd);
      const { publicUrl } = up as any;

      const url = publicUrl as string;
      setIdStatus('completed');
      setIdUrl(url);
      setIdFileName(file.name);
      setIdError(null);
    } catch (err) {
      console.error(err);
      setIdStatus('failed');
      setIdError('Upload failed. Please try again with a clear photo (JPG/PNG/HEIC).');
    } finally {
      if (inputEl) inputEl.value = ''; // safe even after awaits
    }
  };

  // Custom image file picker handler
  const onImagePicked = (imageId: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.currentTarget;
    if (isCompletedSession) {
      inputEl.value = '';
      return;
    }
    const file = inputEl.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      setCustomImages((prev) => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          status: 'failed',
          error: 'Unsupported file. Please upload a photo (JPG/PNG/HEIC).',
        },
      }));
      inputEl.value = '';
      return;
    }

    setCustomImages((prev) => ({
      ...prev,
      [imageId]: {
        url: null,
        status: 'pending',
        fileName: file.name,
        error: null,
      },
    }));

    try {
      // Get capture token for type='custom-image'
      const r = await apiClient.post(`/agreements/selfie/start`, {
        type: 'custom-image',
        imageId,
      });
      const { captureToken } = r as any;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('token', captureToken);

      const up = await apiClient.post(`/agreements/selfie-upload`, fd);
      const { publicUrl } = up as any;

      const url = publicUrl as string;
      setCustomImages((prev) => ({
        ...prev,
        [imageId]: {
          url,
          status: 'completed',
          fileName: file.name,
          error: null,
        },
      }));

      // Trigger UI refresh to show preview
      setCustomImageRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      setCustomImages((prev) => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          status: 'failed',
          error: 'Upload failed. Please try again with a clear photo (JPG/PNG/HEIC).',
        },
      }));

      // Trigger UI refresh to show error
      setCustomImageRefreshTrigger((prev) => prev + 1);
    } finally {
      if (inputEl) inputEl.value = '';
    }
  };

  // BES-9331: gate mirrors the server's attachment extension whitelist (shared
  // constant) plus HEIC/HEIF, which the server transcodes to JPEG.
  const isAllowedAttachmentFile = (file: File): boolean => {
    if (file.type.startsWith('image/') || file.type === 'application/pdf') return true;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return ext === 'heic' || ext === 'heif' || ATTACHMENT_ALLOWED_EXTENSIONS.includes(ext);
  };

  // BES-9331: attachment file picker handler (clone of onImagePicked with type
  // 'attachment'; the server writes progress_json.formUploads itself)
  const onFormAttachmentPicked = (fieldId: string) => async (e: { currentTarget: HTMLInputElement }) => {
    const inputEl = e.currentTarget;
    if (isCompletedSession) {
      inputEl.value = '';
      return;
    }
    const file = inputEl.files?.[0];
    if (!file) return;

    if (!isAllowedAttachmentFile(file)) {
      setFormUploads((prev) => ({
        ...prev,
        [fieldId]: {
          record: null,
          status: 'failed',
          fileName: file.name,
          error: t('Unsupported file. Please upload a PDF or an image.'),
        },
      }));
      inputEl.value = '';
      return;
    }

    setFormUploads((prev) => ({
      ...prev,
      [fieldId]: { record: null, status: 'pending', fileName: file.name, error: null },
    }));

    try {
      const r = await apiClient.post(`/agreements/selfie/start`, { type: 'attachment', imageId: fieldId });
      const { captureToken } = r as any;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('token', captureToken);

      const up = await apiClient.post(`/agreements/selfie-upload`, fd);
      const { publicUrl } = up as any;

      setFormUploads((prev) => ({
        ...prev,
        [fieldId]: {
          record: { url: publicUrl, fileName: file.name, mimeType: file.type || 'application/octet-stream' },
          status: 'completed',
          fileName: file.name,
          error: null,
        },
      }));
    } catch (err) {
      console.error(err);
      setFormUploads((prev) => ({
        ...prev,
        [fieldId]: { record: null, status: 'failed', fileName: file.name, error: t('Upload failed. Please try again.') },
      }));
    } finally {
      if (inputEl) inputEl.value = '';
    }
  };

  // Lazily create (and keep re-pointing to the latest handler closure) the
  // hidden file input backing an attachment component.
  const ensureFormComponentFileInput = (fieldId: string): HTMLInputElement => {
    let input = formComponentFileInputRefs.current[fieldId];
    if (!input) {
      input = document.createElement('input');
      input.type = 'file';
      input.accept = ATTACHMENT_ACCEPT;
      input.style.display = 'none';
      document.body.appendChild(input);
      formComponentFileInputRefs.current[fieldId] = input;
    }
    // Assign (not addEventListener) so each mount pass binds fresh state
    input.onchange = (e) => {
      void onFormAttachmentPicked(fieldId)({ currentTarget: e.currentTarget as HTMLInputElement });
    };
    return input;
  };

  // BES-9331: persist a registry form-component value (progress channel)
  const handleFormComponentSave = async (fieldId: string, value: string) => {
    try {
      if (isCompletedSession) return;
      if (!session || !['sent', 'in_progress'].includes(session.status)) return;

      formComponentValuesRef.current = { ...formComponentValuesRef.current, [fieldId]: value };
      setFormComponentValues((prev) => ({ ...prev, [fieldId]: value }));

      await apiClient.patch(`/agreements/progress`, {
        fieldId,
        value,
        fieldType: 'form-component',
      });
    } catch (error) {
      console.error('Error saving form component value:', error);
    }
  };

  // Render/refresh the ID upload UI or preview each time the page renders
  const mountIdUploadUI = () => {
    document.querySelectorAll('[data-id-id]').forEach((node) => {
      const el = node as HTMLElement;

      // Reset wrapper styles for mobile responsiveness
      el.style.cssText = `${el.style.cssText}; max-width: 100%; box-sizing: border-box;`;

      // If already uploaded, show preview + replace button
      if (idUrl && idStatus === 'completed') {
        const replaceDisabled = isCompletedSession;
        el.innerHTML = `
          <div style="text-align:center;padding:12px;max-width:100%;box-sizing:border-box;">
            <img src="${idUrl}" style="max-width:min(500px, 100%);max-height:500px;width:100%;object-fit:cover;border-radius:6px;border:1px solid #d1d5db" />
            <button type="button" data-id-replace
              ${replaceDisabled ? 'disabled aria-disabled="true"' : ''}
              style="margin-top:10px;background:${replaceDisabled ? '#9ca3af' : '#6b7280'};color:#fff;border:none;border-radius:6px;padding:8px 12px;cursor:${replaceDisabled ? 'not-allowed' : 'pointer'};font-size:.875rem;display:block;margin-left:auto;margin-right:auto;">
              ${escapeHtml(t('Replace image'))}
            </button>
          </div>
        `;
        if (!replaceDisabled) {
          el.querySelector<HTMLButtonElement>('[data-id-replace]')?.addEventListener('click', (e) => {
            e.preventDefault();
            idInputRef.current?.click();
          });
        }
        return;
      }

      // Not uploaded yet: show upload CTA
      const uploadDisabled = isCompletedSession;
      el.innerHTML = `
          <div style="text-align:center;padding:12px;max-width:100%;box-sizing:border-box;">
            <div style="font-size:13px;color:#374151;margin-bottom:8px;">
              ${escapeHtml(t('Upload a clear photo of your government-issued ID'))} <span style="color: #dc2626;">*</span>
            </div>
            <button type="button" data-id-upload-btn
              ${uploadDisabled ? 'disabled aria-disabled="true"' : ''}
              style="display:inline-flex;align-items:center;gap:8px;background:${uploadDisabled ? 'var(--besty-blue-disabled)' : 'var(--besty-blue)'};color:#fff;border:none;border-radius:8px;padding:10px 14px;cursor:${uploadDisabled ? 'not-allowed' : 'pointer'};font-size:.9rem">
              ${escapeHtml(t('Upload ID'))}
            </button>
            ${idError ? `<div style="margin-top:8px;color:#b91c1c;font-size:12px;">${idError}</div>` : ''}
          </div>
        `;
      if (!uploadDisabled) {
        el.querySelector<HTMLButtonElement>('[data-id-upload-btn]')?.addEventListener('click', () => {
          idInputRef.current?.click();
        });
      }
    });
  };

  // Render/refresh the custom image upload UI
  const mountImageUploadUI = () => {
    if (!session) return;

    document.querySelectorAll('[data-image-id]').forEach((node) => {
      const el = node as HTMLElement;
      const imageId = el.getAttribute('data-image-id');

      if (!imageId) return;

      // Prefer the server-translated title from the session payload; the
      // data-image-title attribute stays in the template's source language
      // since Azure doesn't translate custom data-* attributes.
      const sessionTitle = session.imageUploads?.find((u) => u.id === imageId)?.title;
      const title = escapeHtml(decodeHtml(sessionTitle || el.getAttribute('data-image-title') || ''));

      // Detect selfie-type images (front camera) vs regular uploads (rear camera/gallery)
      const isSelfieCapture = el.getAttribute('data-image-capture') === 'user';

      // Ensure a hidden file input exists for this image ID (handles dynamically rendered guest-info images)
      if (!customImageInputRefs.current[imageId]) {
        const inp = document.createElement('input');
        inp.type = 'file';
        inp.accept = 'image/*';
        inp.style.display = 'none';
        if (isCompletedSession) inp.disabled = true;
        inp.addEventListener('change', () => {
          // Delegate to onImagePicked handler
          const syntheticEvent = {
            currentTarget: inp,
            target: inp,
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          onImagePicked(imageId)(syntheticEvent);
        });
        document.body.appendChild(inp);
        customImageInputRefs.current[imageId] = inp;
      }

      // Reset wrapper styles for mobile responsiveness
      el.style.cssText = `${el.style.cssText}; max-width: 100%; box-sizing: border-box;`;

      const imageData = customImages[imageId];
      const isUploaded = imageData?.status === 'completed' && imageData?.url;

      // If already uploaded, show preview + replace button
      if (isUploaded) {
        const replaceDisabled = isCompletedSession;
        // Remove placeholder background and border when showing uploaded image
        el.style.background = 'transparent';
        el.style.border = 'none';
        el.style.padding = '0';

        el.innerHTML = `
          <div style="text-align:center;padding:12px;max-width:100%;box-sizing:border-box;">
            ${title ? `<div style="font-size:14px;color:#374151;margin-bottom:8px;">${title}</div>` : ''}
            <img src="${imageData.url}" style="max-width:min(500px, 100%);max-height:500px;width:100%;object-fit:cover;border-radius:6px;border:1px solid #d1d5db" />
            <button type="button" data-image-replace="${imageId}"
              ${replaceDisabled ? 'disabled aria-disabled="true"' : ''}
              style="margin-top:10px;background:${replaceDisabled ? '#9ca3af' : '#6b7280'};color:#fff;border:none;border-radius:6px;padding:8px 12px;cursor:${replaceDisabled ? 'not-allowed' : 'pointer'};font-size:.875rem;display:block;margin-left:auto;margin-right:auto;">
              ${escapeHtml(t('Replace image'))}
            </button>
          </div>
        `;
        if (!replaceDisabled) {
          el.querySelector<HTMLButtonElement>(`[data-image-replace="${imageId}"]`)?.addEventListener('click', (e) => {
            e.preventDefault();
            if (isSelfieCapture) {
              startGuestInfoSelfieCapture(imageId!);
            } else {
              customImageInputRefs.current[imageId!]?.click();
            }
          });
        }
        return;
      }

      // Not uploaded yet: show upload CTA
      const uploadDisabled = isCompletedSession;
      const error = imageData?.error || null;
      const buttonLabel = isSelfieCapture ? t('Take selfie') : t('Upload image');
      el.innerHTML = `
          <div style="text-align:center;padding:12px;max-width:100%;box-sizing:border-box;">
            ${title ? `<div style="font-size:14px;color:#374151;margin-bottom:8px;">${title}</div>` : ''}
            <button type="button" data-image-upload-btn="${imageId}"
              ${uploadDisabled ? 'disabled aria-disabled="true"' : ''}
              style="display:inline-flex;align-items:center;gap:8px;background:${uploadDisabled ? 'var(--besty-blue-disabled)' : 'var(--besty-blue)'};color:#fff;border:none;border-radius:8px;padding:10px 14px;cursor:${uploadDisabled ? 'not-allowed' : 'pointer'};font-size:.9rem">
              ${buttonLabel}
            </button>
            ${error ? `<div style="margin-top:8px;color:#b91c1c;font-size:12px;">${error}</div>` : ''}
          </div>
        `;
      if (!uploadDisabled) {
        el.querySelector<HTMLButtonElement>(`[data-image-upload-btn="${imageId}"]`)?.addEventListener('click', () => {
          if (isSelfieCapture) {
            // Open camera modal (same as standalone selfie)
            startGuestInfoSelfieCapture(imageId!);
          } else {
            customImageInputRefs.current[imageId!]?.click();
          }
        });
      }
    });
  };

  // Reusable poll helper (singleton)
  const stopSelfiePoll = () => {
    if (selfiePollRef.current) {
      clearInterval(selfiePollRef.current);
      selfiePollRef.current = null;
    }
  };
  const pollSelfieStatus = () => {
    if (!session) return () => {};
    // Avoid duplicate pollers
    stopSelfiePoll();
    let tries = 0;
    const id = window.setInterval(async () => {
      tries++;
      try {
        const r = await apiClient.get(`/agreements/selfie/status`);
        const s = r as any;
        if (s.selfie_status === 'completed' && s.selfie_url) {
          setSelfieStatus('completed');
          setSelfieUrl(s.selfie_url);
          stopSelfiePoll();
        }
      } catch {}
      if (tries > 90) stopSelfiePoll(); // ~3 min
    }, 2000);
    selfiePollRef.current = id as unknown as number;
    return stopSelfiePoll;
  };

  // Start capture helper (reusable for Retake)
  const startSelfieCapture = async () => {
    if (!session || isCompletedSession) return;
    pendingGuestSelfieRef.current = null; // Clear guest-info ref so result routes to global selfie
    const resp = await apiClient.post(`/agreements/selfie/start`, {
      type: 'selfie',
    });
    const { captureToken } = resp as any;

    // NEW: Open modal instead of popup
    setSelfieCaptureToken(captureToken);
    setSelfieModalOpen(true);

    // Start fallback polling
    pollSelfieStatus();
  };

  // Start selfie capture for a guest-info image field (reuses the camera modal)
  const startGuestInfoSelfieCapture = async (imageId: string) => {
    if (!session || isCompletedSession) return;
    const resp = await apiClient.post(`/agreements/selfie/start`, {
      type: 'custom-image',
      imageId,
    });
    const { captureToken } = resp as any;
    pendingGuestSelfieRef.current = imageId;
    setSelfieCaptureToken(captureToken);
    setSelfieModalOpen(true);
    // No fallback polling — pollSelfieStatus only handles global selfie, not custom images.
    // Guest-info selfies are routed via the besty:selfieUploaded message handler.
  };

  // Save a guest-info field value to the backend
  const handleGuestInfoSave = async (fieldId: string, value: string, fieldType: string = 'guest-info-text') => {
    if (isCompletedSession) return;
    setGuestInfoValues((prev) => ({ ...prev, [fieldId]: value }));
    try {
      await apiClient.patch('/agreements/progress', { fieldId, value, fieldType });
    } catch (err) {
      console.error('Failed to save guest info field:', err);
    }
  };

  // Cards shown for one guest-info component: reservation-driven cards in auto
  // mode, otherwise the guest's own count clamped to the component's ceiling
  // (listing max occupancy minus the primary signer in capped mode).
  const resolveGuestCount = (comp: GuestInfoComponent): number => {
    const autoCount = comp.mode === 'auto' ? (comp.guestCount ?? 0) : 0;
    const manualCount = clampGuestInfoCount(guestInfoCounts[comp.id] ?? 0, comp.mode, comp.maxGuestCount);
    return Math.max(autoCount, manualCount);
  };

  // Render guest-info components into [data-guest-info-id] placeholders
  const mountGuestInfoUI = () => {
    if (!session) return;
    const comps = session.guestInfoComponents;
    if (!comps || comps.length === 0) return;

    document.querySelectorAll('[data-guest-info-id]').forEach((node) => {
      const el = node as HTMLElement;
      const compId = el.getAttribute('data-guest-info-id');
      if (!compId) return;

      const comp = comps.find((c) => c.id === compId);
      if (!comp) return;

      const autoCount = comp.mode === 'auto' ? comp.guestCount : 0;
      const guestCount = resolveGuestCount(comp);
      const ceiling = guestInfoCeiling(comp.mode, comp.maxGuestCount);
      // Show "+" button if the guest drives the count, or auto mode resolved to
      // 0 required guests (fallback). Capped mode also stops at its ceiling.
      const guestDriven = isGuestAddableMode(comp.mode) || autoCount === 0;
      const atCeiling = guestCount >= ceiling;
      const disabled = isCompletedSession;

      // Skip rebuild if guest cards are already mounted with the correct count and language
      // This prevents destroying signature pads / selfie previews on unrelated state changes
      const cacheKey = `${guestCount}:${language}`;
      const currentMountedKey = el.getAttribute('data-gi-mounted-count');
      if (currentMountedKey === cacheKey) return;
      el.setAttribute('data-gi-mounted-count', cacheKey);

      el.innerHTML = '';

      // Show helpful message when no guests yet
      if (guestCount === 0) {
        const msg = document.createElement('div');
        msg.style.cssText = 'text-align:center;padding:16px;color:#64748b;font-size:14px;';
        msg.textContent = t('Add guest information');
        el.appendChild(msg);
      }

      // Render guest cards
      for (let n = 0; n < guestCount; n++) {
        const card = document.createElement('div');
        card.style.cssText =
          'border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;margin:12px 0;background:#fff;';

        // Header
        const header = document.createElement('div');
        header.style.cssText =
          'background:#f0f9ff;border-bottom:1px solid #e2e8f0;padding:10px 16px;display:flex;justify-content:space-between;align-items:center;';
        header.innerHTML = `<span style="font-weight:600;font-size:14px;color:#1e293b;">${escapeHtml(comp.label)} ${n + 1}</span>`;

        // Remove button: on last card, only for manually-added guests (manual mode or auto-fallback)
        const isManuallyAdded = n >= autoCount; // guests beyond auto count were added manually
        if (guestDriven && n === guestCount - 1 && isManuallyAdded && !disabled) {
          const removeBtn = document.createElement('button');
          removeBtn.setAttribute('data-remove-guest', String(n));
          removeBtn.type = 'button';
          removeBtn.style.cssText =
            'background:none;border:none;color:#94a3b8;font-size:18px;cursor:pointer;padding:2px 6px;line-height:1;border-radius:4px;';
          removeBtn.textContent = '\u2715';
          removeBtn.addEventListener('click', () => {
            setGuestInfoCounts((prev) => {
              const newCount = Math.max(0, (prev[compId] ?? 0) - 1);
              apiClient
                .patch('/agreements/progress', {
                  fieldId: compId,
                  value: String(newCount),
                  fieldType: 'guest-info-meta',
                })
                .catch((err: unknown) => console.error('Failed to save guest count:', err));
              return { ...prev, [compId]: newCount };
            });
          });
          header.appendChild(removeBtn);
        }

        card.appendChild(header);

        // Fields container
        const fieldsDiv = document.createElement('div');
        fieldsDiv.style.cssText = 'padding:16px;padding-bottom:48px;display:flex;flex-direction:column;gap:12px;';

        comp.fields.forEach((field, fieldIndex) => {
          const fieldId = `gi-${compId}-g${n}-${field.type}-${fieldIndex}`;
          const savedVal = guestInfoValues[fieldId] || '';
          const fieldWrapper = document.createElement('div');

          const labelText =
            field.label || field.type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
          const requiredMark = field.required ? ' <span style="color:#dc2626;">*</span>' : '';
          const labelHtml = `<label style="display:block;font-size:13px;color:#64748b;margin-bottom:4px;text-align:left;">${escapeHtml(labelText)}${requiredMark}</label>`;

          const disabledAttr = disabled ? 'disabled' : '';
          const disabledStyle = disabled ? 'background:#f9fafb;cursor:not-allowed;' : '';
          const inputStyle = `width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;box-sizing:border-box;${disabledStyle}`;
          const placeholder = t('Enter {{label}}', { label: labelText.toLowerCase() });

          if (field.type === 'text') {
            fieldWrapper.innerHTML = `${labelHtml}<input type="text" data-gi-field="${escapeHtml(fieldId)}" value="${escapeHtml(savedVal)}" placeholder="${escapeHtml(placeholder)}" ${disabledAttr} style="${inputStyle}" />`;
          } else if (field.type === 'date') {
            fieldWrapper.innerHTML = `${labelHtml}<input type="date" data-gi-field="${escapeHtml(fieldId)}" value="${escapeHtml(savedVal)}" ${disabledAttr} style="${inputStyle}" />`;
          } else if (field.type === 'email') {
            fieldWrapper.innerHTML = `${labelHtml}<input type="email" data-gi-field="${escapeHtml(fieldId)}" value="${escapeHtml(savedVal)}" placeholder="${escapeHtml(t('Enter email address'))}" ${disabledAttr} style="${inputStyle}" />`;
          } else if (field.type === 'phone') {
            fieldWrapper.innerHTML = `${labelHtml}<input type="tel" data-gi-field="${escapeHtml(fieldId)}" value="${escapeHtml(savedVal)}" placeholder="${escapeHtml(t('Enter phone number'))}" ${disabledAttr} style="${inputStyle}" />`;
          } else if (field.type === 'signature') {
            const sigFieldId = `gi-${compId}-g${n}-sig-${fieldIndex}`;
            fieldWrapper.innerHTML = `${labelHtml}<div data-signature-id="${escapeHtml(sigFieldId)}" data-lock-on-sign="true" data-signer="guest" data-required="${field.required}" style="width:100%;max-width:320px;min-height:120px;height:160px;display:block;position:relative;"></div>`;
          } else if (field.type === 'initials') {
            const initFieldId = `gi-${compId}-g${n}-init-${fieldIndex}`;
            fieldWrapper.innerHTML = `${labelHtml}<div data-initials-id="${escapeHtml(initFieldId)}" data-required="${field.required}" style="display:block;width:100%;max-width:600px;"></div>`;
          } else if (field.type === 'selfie') {
            // Use image upload component — label goes into data-image-title (mountImageUploadUI renders it)
            const selfieFieldId = `gi-${compId}-g${n}-selfie-${fieldIndex}`;
            const selfieTitle = field.label || t('Take a clear selfie for identity verification');
            fieldWrapper.style.cssText = 'clear:both;padding-top:8px;';
            fieldWrapper.innerHTML = `<div data-image-id="${escapeHtml(selfieFieldId)}" data-image-title="${escapeHtml(selfieTitle)}${field.required ? ' *' : ''}" data-image-capture="user" data-required="${field.required}" style="width:100%;"></div>`;
          } else if (field.type === 'id_upload') {
            // Use image upload component — label goes into data-image-title
            const idFieldId = `gi-${compId}-g${n}-idupload-${fieldIndex}`;
            const idTitle = field.label || t('Upload a clear photo of your government-issued ID');
            fieldWrapper.style.cssText = 'clear:both;padding-top:8px;';
            fieldWrapper.innerHTML = `<div data-image-id="${escapeHtml(idFieldId)}" data-image-title="${escapeHtml(idTitle)}${field.required ? ' *' : ''}" data-required="${field.required}" style="width:100%;"></div>`;
          } else if (field.type === 'image_upload') {
            const imgFieldId = `gi-${compId}-g${n}-img-${fieldIndex}`;
            const imgTitle = field.label || t('Upload image');
            fieldWrapper.style.cssText = 'clear:both;padding-top:8px;';
            fieldWrapper.innerHTML = `<div data-image-id="${escapeHtml(imgFieldId)}" data-image-title="${escapeHtml(imgTitle)}${field.required ? ' *' : ''}" data-required="${field.required}" style="width:100%;"></div>`;
          } else if (field.type === 'checkbox') {
            const chkFieldId = `gi-${compId}-g${n}-chk-${fieldIndex}`;
            fieldWrapper.innerHTML = `<div data-checkbox-id="${escapeHtml(chkFieldId)}" data-label="${escapeHtml(field.label || '')}" data-required="${field.required}"></div>`;
          } else {
            // Fallback for any unknown type - render as text input
            fieldWrapper.innerHTML = `${labelHtml}<input type="text" data-gi-field="${escapeHtml(fieldId)}" value="${escapeHtml(savedVal)}" placeholder="${escapeHtml(labelText)}" ${disabledAttr} style="${inputStyle}" />`;
          }

          fieldsDiv.appendChild(fieldWrapper);
        });

        card.appendChild(fieldsDiv);
        el.appendChild(card);

        // Attach event listeners for text/date/email/phone fields (skip if disabled)
        if (!disabled) {
          card.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-gi-field]').forEach((inp) => {
            const fId = inp.getAttribute('data-gi-field') || '';
            const fType = inp.type === 'date' ? 'guest-info-date' : 'guest-info-text';

            if (inp.type === 'date') {
              inp.addEventListener('change', () => handleGuestInfoSave(fId, inp.value, fType));
            } else {
              inp.addEventListener('blur', () => handleGuestInfoSave(fId, inp.value.trim(), fType));
              inp.addEventListener('input', () => {
                clearTimeout((inp as any)._giSaveTimeout);
                (inp as any)._giSaveTimeout = setTimeout(() => handleGuestInfoSave(fId, inp.value.trim(), fType), 1000);
              });
            }
          });
        }
      }

      // Add Guest button (guest-driven modes or auto-fallback when 0 required guests).
      // At a capped component's ceiling the button stays visible but greyed out,
      // with a tooltip and a hint explaining why.
      // BES-9726: a unit that sleeps 1 has a ceiling of 0, so there is nothing to
      // add and no limit to explain. Drop the control instead of showing it dead.
      if (guestDriven && !disabled && ceiling > 0) {
        const addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.setAttribute('data-add-guest', compId);
        // Only capped mode's ceiling comes from the listing. Manual mode hits the
        // absolute 25-card limit, so it gets the generic wording.
        const maxedMessage =
          comp.mode === CAPPED_GUEST_INFO_MODE
            ? t('You have added the maximum number of guests allowed for this listing')
            : t('You have added the maximum number of guests allowed');
        if (atCeiling) {
          addBtn.disabled = true;
          addBtn.title = maxedMessage;
          addBtn.style.cssText =
            'display:flex;align-items:center;justify-content:center;gap:6px;width:100%;padding:10px;border:1px dashed #e2e8f0;border-radius:8px;background:#f9fafb;color:#cbd5e1;font-size:14px;cursor:not-allowed;margin-top:8px;';
          addBtn.textContent = '+ ' + t('Add Guest');
          el.appendChild(addBtn);

          const hint = document.createElement('div');
          hint.style.cssText = 'text-align:center;color:#94a3b8;font-size:12px;margin-top:6px;';
          hint.textContent = maxedMessage;
          el.appendChild(hint);
          return;
        }
        addBtn.style.cssText =
          'display:flex;align-items:center;justify-content:center;gap:6px;width:100%;padding:10px;border:1px dashed #94a3b8;border-radius:8px;background:#fff;color:#64748b;font-size:14px;cursor:pointer;margin-top:8px;';
        addBtn.textContent = '+ ' + t('Add Guest');
        addBtn.addEventListener('click', () => {
          setGuestInfoCounts((prev) => {
            const current = prev[compId] ?? 0;
            if (current >= ceiling) return prev;
            const newCount = current + 1;
            apiClient
              .patch('/agreements/progress', {
                fieldId: compId,
                value: String(newCount),
                fieldType: 'guest-info-meta',
              })
              .catch((err: unknown) => console.error('Failed to save guest count:', err));
            return { ...prev, [compId]: newCount };
          });
        });
        el.appendChild(addBtn);
      }
    });
  };

  // Map field type keys to user-friendly labels
  // Inject UI into every [data-selfie-id] on the current page
  const mountSelfieUI = () => {
    document.querySelectorAll('[data-selfie-id]').forEach((node) => {
      const el = node as HTMLElement;

      // Reset wrapper styles for mobile responsiveness
      el.style.cssText = `${el.style.cssText}; max-width: 100%; box-sizing: border-box;`;

      // Already verified: show preview + Retake
      if (selfieUrl) {
        const retakeDisabled = isCompletedSession;
        el.innerHTML = `
          <div style="text-align:center;padding:12px;max-width:100%;box-sizing:border-box;">
            <img src="${selfieUrl}" style="max-width:min(420px, 100%);max-height:420px;width:100%;object-fit:cover;border-radius:6px;border:1px solid #d1d5db" />
            <button type="button" data-selfie-retake
              ${retakeDisabled ? 'disabled aria-disabled="true"' : ''}
              style="margin-top:10px;background:${retakeDisabled ? '#9ca3af' : '#6b7280'};color:#fff;border:none;border-radius:6px;padding:8px 12px;cursor:${retakeDisabled ? 'not-allowed' : 'pointer'};font-size:.875rem;display:block;margin-left:auto;margin-right:auto;">
              ${escapeHtml(t('Retake selfie'))}
            </button>
          </div>
        `;
        if (!retakeDisabled) {
          el.querySelector<HTMLButtonElement>('[data-selfie-retake]')?.addEventListener('click', (e) => {
            e.preventDefault();
            startSelfieCapture();
          });
        }
        return;
      }
      // Not done yet: show in-place capture CTA
      const startDisabled = isCompletedSession;
      el.innerHTML = `
        <div style="text-align:center;padding:12px;max-width:100%;box-sizing:border-box;">
          <div style="font-size:13px;color:#374151;margin-bottom:8px;">
            ${escapeHtml(t('Take a clear selfie for identity verification'))} <span style="color: #dc2626;">*</span>
          </div>
          <button type="button" data-selfie-start
            ${startDisabled ? 'disabled aria-disabled="true"' : ''}
            style="display:inline-flex;align-items:center;gap:8px;background:${startDisabled ? 'var(--besty-blue-disabled)' : 'var(--besty-blue)'};color:#fff;border:none;border-radius:8px;padding:10px 14px;cursor:${startDisabled ? 'not-allowed' : 'pointer'};font-size:.9rem">
            ${escapeHtml(t('Take selfie'))}
          </button>
          <div style="margin-top:8px;color:#6b7280;font-size:12px;">${escapeHtml(t('Opens camera; image is embedded in the agreement'))}</div>
        </div>
      `;
      if (!startDisabled) {
        el.querySelector<HTMLButtonElement>('[data-selfie-start]')?.addEventListener('click', () => {
          startSelfieCapture();
        });
      }
    });
  };

  // Helper to check if a field is required
  const isFieldRequired = (fieldId: string, fieldType: 'signature' | 'initials' | 'input'): boolean => {
    if (!session) return true; // Default to required if no session
    if (fieldType === 'signature') {
      const field = session.signatureFields?.find((f) => f.id === fieldId);
      return field ? field.required : true; // Default to required for backward compatibility
    }
    if (fieldType === 'initials') {
      const field = session.initialFields?.find((f) => f.id === fieldId);
      return field ? field.required : true;
    }
    if (fieldType === 'input') {
      const field = session.inputFields?.find((f) => f.id === fieldId);
      return field ? field.required : true;
    }
    return true;
  };

  // Render/refresh the initials UI with a nice styled component
  const mountInitialsUI = () => {
    document.querySelectorAll('[data-initials-id]').forEach((node) => {
      const el = node as HTMLElement;
      const initialsId = el.getAttribute('data-initials-id');
      if (!initialsId) return;

      const savedInitials = initials[initialsId];
      const currentlyDisplayed = el.dataset.initialsValue;
      const isRequired = isFieldRequired(initialsId, 'initials');

      // Skip if already showing the correct state (saved value matches displayed value)
      if (el.dataset.initialsUiMounted === 'true' && currentlyDisplayed === (savedInitials || '')) {
        return;
      }

      el.dataset.initialsUiMounted = 'true';
      el.dataset.initialsValue = savedInitials || '';

      // If already completed, show the completed state
      if (savedInitials) {
        // Override outer element styles to ensure consistent display (fixes old saved template styles)
        el.style.display = 'block';
        el.style.width = '100%';
        el.style.maxWidth = '600px';
        el.style.border = 'none';
        el.style.background = 'transparent';
        el.style.padding = '0';
        el.style.margin = '10px 0';

        el.innerHTML = `
          <div style="
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 60px;
            padding: 12px 16px;
            background: #f0fdf4;
            border: 2px solid #059669;
            border-radius: 10px;
            gap: 8px;
          ">
            <span style="
              font-size: 24px;
              font-weight: 700;
              font-family: 'Times New Roman', Georgia, serif;
              font-style: italic;
              color: #1a1a1a;
              letter-spacing: 1px;
            ">${savedInitials}</span>
            <span style="
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 22px;
              height: 22px;
              background: #059669;
              border-radius: 50%;
              color: white;
              font-size: 14px;
            ">✓</span>
          </div>
        `;
        el.style.pointerEvents = 'none';
        el.style.cursor = 'default';
        return;
      }

      // Not completed - show the action prompt with nice styling
      const isDisabled = isCompletedSession;

      // Override outer element styles to ensure consistent display (fixes old saved template styles)
      el.style.display = 'block';
      el.style.width = '100%';
      el.style.maxWidth = '600px';
      el.style.border = 'none';
      el.style.background = 'transparent';
      el.style.padding = '0';
      el.style.margin = '10px 0';

      el.innerHTML = `
        <div data-initials-action style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 80px;
          padding: 16px 20px;
          background: ${isDisabled ? '#f9fafb' : '#ffffff'};
          border: 2px dashed ${isDisabled ? '#d1d5db' : '#94a3b8'};
          border-radius: 10px;
          cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
          transition: all 0.2s ease;
          gap: 6px;
        ">
          <div style="
            font-size: 13px;
            color: ${isDisabled ? '#9ca3af' : '#64748b'};
            font-weight: 500;
          ">${escapeHtml(t('Click to add initials'))}${isRequired ? ' <span style="color: #dc2626;">*</span>' : ''}</div>
          <div style="
            font-size: 26px;
            font-weight: 700;
            font-family: 'Times New Roman', Georgia, serif;
            font-style: italic;
            color: ${isDisabled ? '#d1d5db' : '#cbd5e1'};
            letter-spacing: 2px;
          ">___</div>
        </div>
      `;

      // Add hover effect only if not disabled
      if (!isDisabled) {
        const actionDiv = el.querySelector('[data-initials-action]') as HTMLElement;
        if (actionDiv) {
          actionDiv.addEventListener('mouseenter', () => {
            actionDiv.style.borderColor = 'var(--besty-blue, #2563eb)';
            actionDiv.style.background = '#f8fafc';
          });
          actionDiv.addEventListener('mouseleave', () => {
            actionDiv.style.borderColor = '#94a3b8';
            actionDiv.style.background = '#ffffff';
          });
        }

        // Handle click
        el.addEventListener('click', () => handleInitials(initialsId));
      }
    });
  };

  // Re-apply verification UIs on page change or when state updates
  // Helper to fix mobile layout for side-by-side IMAGE containers only
  // NOTE: Do NOT include signature pads here - they have their own button layout
  const fixMobileImageLayout = () => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    // Only target image upload elements, NOT signatures
    const imageSelectors = ['[data-selfie-id]', '[data-id-id]', '[data-image-id]'];

    imageSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        // Get parent and check if it's a flex container with multiple image children
        const parent = el.parentElement;
        if (!parent) return;

        // Skip if parent contains a signature pad (don't break signature button layout)
        if (parent.querySelector('[data-signature-id]')) return;

        const computedStyle = window.getComputedStyle(parent);
        const totalImageChildren = imageSelectors.reduce((sum, s) => sum + parent.querySelectorAll(s).length, 0);
        const hasMultipleImageChildren = totalImageChildren > 1;

        // If parent is flex/grid and has multiple image children, stack them
        if ((computedStyle.display === 'flex' || computedStyle.display === 'grid') && hasMultipleImageChildren) {
          parent.style.cssText = `${parent.style.cssText}; display: block !important; height: auto !important;`;
        }

        // Make sure image containers take full width
        const htmlEl = el as HTMLElement;
        htmlEl.style.cssText = `${htmlEl.style.cssText}; width: 100% !important; max-width: 100% !important; margin: 16px 0 !important; display: block !important;`;
      });
    });
  };

  useEffect(() => {
    if (!session) return;
    const t = setTimeout(() => {
      mountGuestInfoUI(); // Must run FIRST — renders data-*-id elements that other mount functions need to find
      mountSelfieUI(); // Place UI where the host dropped the selfie block
      mountIdUploadUI(); // Place UI where the host dropped the ID block
      mountImageUploadUI(); // Place UI where the host dropped custom image blocks
      mountInitialsUI(); // Place UI where the host dropped the initials block
      fixMobileImageLayout(); // Fix side-by-side images on mobile
    }, 0);
    return () => clearTimeout(t);
  }, [
    session,
    currentPageIndex,
    selfieUrl,
    idStatus,
    idUrl,
    idFileName,
    idError,
    customImageRefreshTrigger,
    initials,
    guestInfoCounts,
  ]);

  // BES-9331: hydrate registry form components (phone, email, attachment,
  // address). Value inputs mount once per injected page (dataset guard) so
  // re-renders never clobber typing; attachment cards rebuild from upload
  // state on every pass, mirroring the custom-image mount.
  const mountFormComponentsUI = () => {
    if (!session) return;
    document.querySelectorAll('[data-form-component-id]').forEach((node) => {
      const el = node as HTMLElement;
      const fieldId = el.getAttribute('data-form-component-id');
      if (!fieldId) return;
      // The session payload carries the server-translated label; skip markers
      // the server did not surface (unknown/newer types degrade gracefully).
      const field = session.formComponents?.find((f) => f.id === fieldId);
      if (!field) return;
      const descriptor = getFormComponentDescriptor(field.type);
      if (!descriptor) return;

      if (descriptor.valueChannel === 'upload') {
        mountFormComponentAttachment({
          el,
          field,
          upload: formUploads[fieldId],
          disabled: isCompletedSession,
          t,
          onPickFile: () => ensureFormComponentFileInput(fieldId).click(),
        });
        return;
      }

      if (el.dataset.formComponentMounted === 'true') {
        if (isCompletedSession) {
          el.querySelectorAll('input,select').forEach((inp) => {
            (inp as HTMLInputElement).disabled = true;
          });
        }
        return;
      }
      el.dataset.formComponentMounted = 'true';

      const options = {
        el,
        field,
        initialValue: formComponentValuesRef.current[fieldId] || '',
        disabled: isCompletedSession,
        t,
        onSave: (value: string) => handleFormComponentSave(fieldId, value),
        registerFlush: (flush: () => void | Promise<void>) => {
          formComponentFlushersRef.current[fieldId] = flush;
        },
      };
      if (field.type === 'address-input') {
        mountFormComponentAddress(options);
      } else {
        mountFormComponentTextInput(options);
      }
    });
  };

  useEffect(() => {
    if (!session) return;
    const timer = setTimeout(() => mountFormComponentsUI(), 0);
    return () => clearTimeout(timer);
  }, [session, currentPageIndex, formUploads, completed]);

  // Re-check selfie status on window focus (covers when popup closes and message was missed)
  useEffect(() => {
    if (!session) return;
    const onFocus = () => {
      // Only poll if selfie is actually required
      if (session.selfieRequired && selfieStatus !== 'completed' && !selfieUrl) {
        pollSelfieStatus();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [session, selfieStatus, selfieUrl]);

  // Function to initialize signature pads on the page
  const initializeSignaturePads = () => {
    // @ts-ignore
    const SignaturePadCtor = window.SignaturePad;
    if (!SignaturePadCtor) {
      console.error('SignaturePad library not loaded');
      return;
    }

    document.querySelectorAll('[data-signature-id]').forEach((wrapper) => {
      const w = wrapper as HTMLElement;
      if (w.dataset.initialized) return; // Prevent re-initialization
      w.dataset.initialized = 'true';

      console.log('Processing signature component:', w.dataset.signatureId);
      console.log('Component HTML:', w.outerHTML);

      // 1) Ensure there is a dedicated pad box inside the wrapper
      let root = w.querySelector('.sig-root') as HTMLElement | null;
      if (!root) {
        // Ensure wrapper itself has sane dimensions (for legacy templates)
        if (!w.style.height || w.offsetHeight < 40) w.style.height = '160px';
        if (!w.style.width) w.style.width = '100%';
        if (!w.style.maxWidth) w.style.maxWidth = '320px';
        if (!w.style.minHeight) w.style.minHeight = '120px';
        if (!w.style.display) w.style.display = 'block';

        // Create a real inner pad box to contain the canvas
        root = document.createElement('div');
        root.className = 'sig-root';
        Object.assign(root.style, {
          position: 'relative',
          width: '100%',
          height: '100%', // Fill wrapper height
          minHeight: '120px',
          background: '#fff',
          border: '2px dashed #cbd5e1',
          borderRadius: '10px',
        });
        w.insertBefore(root, w.firstChild); // toolbar stays below
      }

      root.style.position = root.style.position || 'relative';
      // 2) Move any stray images that are direct children of the wrapper into the pad container
      Array.from(w.querySelectorAll(':scope > img')).forEach((img) => {
        if (!root.contains(img)) {
          img.classList.add('sig-img');
          root.appendChild(img);
        }
      });

      // 3) Ensure a single preview image lives inside the pad
      let sigImg = root.querySelector('.sig-img') as HTMLImageElement | null;
      if (!sigImg) {
        sigImg = document.createElement('img');
        sigImg.className = 'sig-img';
        sigImg.style.display = 'none';
        root.appendChild(sigImg);
      } else {
        // remove any extra sig-imgs
        Array.from(root.querySelectorAll('.sig-img')).forEach((el, i) => {
          if (i > 0) el.remove();
        });
      }

      // 4) Ensure hint/toolbar exist in root
      const ensureIn = <T extends HTMLElement>(
        parent: HTMLElement,
        el: T | null,
        tag: string,
        className: string,
        styles?: Partial<CSSStyleDeclaration>
      ) => {
        if (el) {
          // If the element exists but is not under the intended parent, move it
          if (!parent.contains(el)) {
            parent.appendChild(el);
          }
          // Ensure it has the expected class and styles (even for pre-existing nodes from the editor)
          if (className) el.classList.add(...className.split(' ').filter(Boolean));
          if (styles) Object.assign(el.style, styles);
          return el as T;
        }
        const created = document.createElement(tag) as T;
        if (className) created.className = className;
        if (styles) Object.assign(created.style, styles);
        parent.appendChild(created);
        return created;
      };

      const hint = ensureIn<HTMLElement>(root, root.querySelector('.sig-hint') as HTMLElement, 'div', 'sig-hint', {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      });
      hint.textContent = t('Click or tap to sign');

      const bar = ensureIn<HTMLElement>(w, w.querySelector('.sig-toolbar') as HTMLElement, 'div', 'sig-toolbar', {
        display: 'flex',
        gap: '8px',
        marginTop: '8px',
        marginBottom: '32px',
        justifyContent: 'flex-end',
      });

      // Find existing buttons anywhere, but ensure they end up inside the toolbar
      const undoBtn = ensureIn<HTMLButtonElement>(
        bar,
        (bar.querySelector('.sig-undo') || w.querySelector('.sig-undo')) as HTMLButtonElement,
        'button',
        'sig-undo'
      );
      const clearBtn = ensureIn<HTMLButtonElement>(
        bar,
        (bar.querySelector('.sig-clear') || w.querySelector('.sig-clear')) as HTMLButtonElement,
        'button',
        'sig-clear'
      );
      const okBtn = ensureIn<HTMLButtonElement>(
        bar,
        (bar.querySelector('.sig-confirm') || w.querySelector('.sig-confirm')) as HTMLButtonElement,
        'button',
        'sig-confirm'
      );

      undoBtn.textContent = t('Undo');
      clearBtn.textContent = t('Clear');
      okBtn.textContent = t('Confirm');

      // Style the buttons to look like proper buttons
      Object.assign(undoBtn.style, {
        padding: '4px 8px',
        border: '1px solid #cbd5e1',
        background: '#fff',
        borderRadius: '4px',
        fontSize: '12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
      });

      Object.assign(clearBtn.style, {
        padding: '4px 8px',
        border: '1px solid #cbd5e1',
        background: '#fff',
        borderRadius: '4px',
        fontSize: '12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
      });

      Object.assign(okBtn.style, {
        padding: '4px 8px',
        border: '1px solid #059669',
        background: '#059669',
        color: '#fff',
        borderRadius: '4px',
        fontSize: '12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
      });

      console.log('Initializing signature pad with proper structure:', w.dataset.signatureId);

      const sigRoot = root;
      const sigHint = hint;
      const sigBar = bar;
      const sigUndoBtn = undoBtn;
      const sigClearBtn = clearBtn;
      const sigOkBtn = okBtn;

      // Add visual test to verify button positions
      console.log('Button positions:', {
        undo: sigUndoBtn.getBoundingClientRect(),
        clear: sigClearBtn.getBoundingClientRect(),
        confirm: sigOkBtn.getBoundingClientRect(),
      });

      const locked = () => w.dataset.lockOnSign === 'true' && w.dataset.signed === 'true';

      // Check if this signature was already saved in the session
      const signatureId = w.getAttribute('data-signature-id') || w.dataset.signatureId || '';
      if (!signatureId) return;
      const savedSignature = signatures[signatureId!];

      if (savedSignature) {
        // Display the saved signature and lock the component
        w.dataset.image = savedSignature;
        w.dataset.signed = 'true';
        sigImg.src = savedSignature;
        sigImg.style.display = 'block';
        sigHint.style.display = 'none';

        // Add the "Signed" indicator
        const existingIndicator = sigRoot.querySelector('.signed-indicator');
        if (!existingIndicator) {
          const signedIndicator = document.createElement('div');
          signedIndicator.className = 'signed-indicator';
          signedIndicator.style.cssText = `
            position: absolute;
            top: 8px;
            right: 8px;
            background: #059669;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            z-index: 30;
          `;
          signedIndicator.textContent = '✓ ' + t('Signed');
          sigRoot.appendChild(signedIndicator);
        }

        console.log('🔄 Rehydrated saved signature:', signatureId);
      } else if (w.dataset.image) {
        // Fallback to component's data-image attribute
        sigImg.src = w.dataset.image;
        sigImg.style.display = 'block';
        sigHint.style.display = 'none';
      }

      const canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.touchAction = 'none';
      canvas.style.zIndex = '1';
      root.appendChild(canvas);

      // Ensure the pad box clips strokes and the bar remains outside
      sigRoot.style.overflow = 'hidden';
      sigRoot.style.position = 'relative';

      // Keep toolbar absolute overlay and above the canvas
      sigBar.style.zIndex = '999';
      sigBar.style.pointerEvents = 'auto';
      sigUndoBtn.style.zIndex = '1000';
      sigUndoBtn.style.pointerEvents = 'auto';
      sigClearBtn.style.zIndex = '1000';
      sigClearBtn.style.pointerEvents = 'auto';
      sigOkBtn.style.zIndex = '1000';
      sigOkBtn.style.pointerEvents = 'auto';
      canvas.style.zIndex = '1';

      // 1) Define refreshUI BEFORE it's ever used
      function refreshUI() {
        const empty = signaturePad.isEmpty();
        const isLocked = locked();
        const hasSignature = sigImg && sigImg.src && sigImg.style.display === 'block';

        // Show toolbar unless locked (HTML sets display:none, so we must flip it on)
        sigBar.style.display = isLocked ? 'none' : 'flex';
        sigHint.style.display = empty && !hasSignature ? 'flex' : 'none';
        sigUndoBtn.disabled = empty;
        sigOkBtn.disabled = empty;

        // If signature is saved and locked, disable the canvas
        if (isLocked && hasSignature) {
          canvas.style.pointerEvents = 'none';
        } else {
          canvas.style.pointerEvents = 'auto';
        }
      }

      // 2) Create signature pad
      const signaturePad = new SignaturePadCtor(canvas, {
        penColor: '#0f172a',
        minWidth: 0.5,
        maxWidth: 2.5,
      });

      // 3) fit() comes AFTER refreshUI is defined
      function fit() {
        const rect = sigRoot.getBoundingClientRect();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);

        // preserve strokes across resize
        const data = signaturePad.toData();

        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        const ctx = canvas.getContext('2d');
        ctx?.scale(ratio, ratio);

        signaturePad.clear();
        if (data && data.length) signaturePad.fromData(data);

        refreshUI();
      }

      // Store resize handler for cleanup
      resizeHandlersRef.current.push(fit);
      window.addEventListener('resize', fit);
      fit(); // safe now

      // 4) Wire stroke events so buttons enable after drawing
      if ((signaturePad as any).addEventListener) {
        (signaturePad as any).addEventListener('beginStroke', refreshUI);
        (signaturePad as any).addEventListener('endStroke', refreshUI);
      } else {
        // older signature_pad fallback
        // @ts-ignore
        signaturePad.onBegin = refreshUI;
        // @ts-ignore
        signaturePad.onEnd = refreshUI;
      }

      sigUndoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        console.log('🔄 Undo button clicked');
        const data = signaturePad.toData();
        if (!data.length) return;
        data.pop();
        signaturePad.fromData(data);
        refreshUI();
      });

      sigClearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        console.log('🗑️ Clear button clicked');
        if (locked()) return;
        signaturePad.clear();
        refreshUI();
      });

      sigOkBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (signaturePad.isEmpty()) return;

        console.log('🖊️ Confirm button clicked for signature:', w.dataset.signatureId);

        // Disable the confirm button to prevent double-clicks
        sigOkBtn.disabled = true;
        sigOkBtn.textContent = t('Saving...');

        try {
          const dataUrl = signaturePad.toDataURL();
          console.log('📝 Generated signature data URL (length):', dataUrl.length);

          const requestUrl = `/agreements/progress`;
          const requestBody = {
            fieldId: w.dataset.signatureId,
            value: dataUrl,
            fieldType: 'signature',
          };

          console.log('🌐 Making PATCH request to:', requestUrl);
          console.log('📦 Request body:', { ...requestBody, value: `[${dataUrl.length} chars]` });

          // Save to backend first
          const responseData = await apiClient.patch(requestUrl, {
            fieldId: w.dataset.signatureId,
            value: dataUrl,
            fieldType: 'signature',
          });

          console.log('✅ Save successful:', responseData);

          // Only update UI if save was successful
          w.dataset.image = dataUrl;
          w.dataset.signed = 'true';
          sigImg.src = dataUrl;
          sigImg.style.display = 'block';
          sigHint.style.display = 'none';

          // Lock and reveal image inside the pad
          signaturePad.off(); // Disable all drawing
          canvas.style.pointerEvents = 'none'; // Disable canvas interactions
          canvas.style.display = 'none'; // Hide canvas, show image
          sigBar.style.display = 'none'; // Hide toolbar

          // Hide any IMG that is still outside the pad container
          Array.from(w.querySelectorAll(':scope > img')).forEach((img) => {
            if (!root.contains(img)) (img as HTMLImageElement).style.display = 'none';
          });

          // Add visual indicator that it's signed and locked
          const signedIndicator = document.createElement('div');
          signedIndicator.style.cssText = `
            position: absolute;
            top: 8px;
            right: 8px;
            background: #059669;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            z-index: 30;
          `;
          signedIndicator.textContent = '✓ ' + t('Signed');
          sigRoot.appendChild(signedIndicator);

          // Update local state
          const detail = {
            signatureId: w.dataset.signatureId,
            signer: w.dataset.signer || 'guest',
            image: dataUrl,
            width: sigRoot.clientWidth,
            height: sigRoot.clientHeight,
          };

          // Notify the React component
          if ((window as any).onSignatureConfirm) {
            (window as any).onSignatureConfirm(detail);
          }

          console.log('✅ Signature confirmed and saved:', w.dataset.signatureId);
        } catch (error) {
          console.error('Failed to save signature:', error);
          alert(t('Failed to save signature. Please try again.'));

          // Re-enable the button on error
          sigOkBtn.disabled = false;
          sigOkBtn.textContent = t('Confirm');
        }
      });

      refreshUI();
    });
  };

  // Initialize typed signature components
  const initializeTypedSignatures = () => {
    // Load Dancing Script font for typed signatures if not already loaded
    if (!document.getElementById('typed-sig-font')) {
      const link = document.createElement('link');
      link.id = 'typed-sig-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap';
      document.head.appendChild(link);
    }

    document.querySelectorAll('[data-typed-signature-id]').forEach((wrapper) => {
      const w = wrapper as HTMLElement;
      // Re-initialize when the session has completed since the last run so the
      // editable "Sign now" UI is torn down. Without this, an optional typed
      // signature left blank keeps showing its input/buttons after completion
      // (the early return would skip the completed-session branch below).
      if (w.dataset.tsInitialized === 'true' && w.dataset.tsCompleted === String(isCompletedSession)) return;
      w.dataset.tsInitialized = 'true';
      w.dataset.tsCompleted = String(isCompletedSession);

      const fieldId = w.getAttribute('data-typed-signature-id') || '';
      if (!fieldId) return;

      const fieldConfig = (session?.typedSignatureFields || []).find((f) => f.id === fieldId);
      const shouldPopulateName = fieldConfig?.populateGuestName !== false;

      // Clear inner placeholder from editor
      w.innerHTML = '';
      w.style.display = 'flex';
      w.style.flexDirection = 'column';
      w.style.alignItems = 'stretch';
      w.style.justifyContent = 'flex-start';
      w.style.padding = '0';
      w.style.gap = '0';
      w.style.fontFamily = 'inherit';
      w.style.width = '100%';
      w.style.minWidth = '280px';
      w.style.height = 'auto';
      w.style.minHeight = 'auto';
      w.style.border = '1px solid #e2e8f0';
      w.style.borderRadius = '8px';
      w.style.overflow = 'hidden';
      w.style.background = '#fff';

      const savedValue = typedSignatures[fieldId];

      if (savedValue) {
        renderTypedSignatureLocked(w, savedValue);
        return;
      }

      // Completed session with no saved value: optional field left blank
      if (isCompletedSession) return;

      // "Type your name" label + input at top
      const inputSection = document.createElement('div');
      inputSection.style.cssText = 'padding:12px 16px;border-bottom:1px solid #e2e8f0;';

      const label = document.createElement('div');
      label.textContent = t('Type your name');
      label.style.cssText = 'font-size:13px;color:#64748b;margin-bottom:6px;font-weight:500;';
      inputSection.appendChild(label);

      const input = document.createElement('input');
      input.type = 'text';
      input.placeholder = t('Enter your full name');
      input.value = shouldPopulateName ? (session?.guestName || '') : '';
      input.style.cssText =
        'width:100%;padding:8px 0;border:none;border-bottom:1px solid #cbd5e1;font-size:15px;font-family:inherit;outline:none;background:transparent;box-sizing:border-box;';
      input.addEventListener('focus', () => { input.style.borderBottomColor = '#3b82f6'; });
      input.addEventListener('blur', () => { input.style.borderBottomColor = '#cbd5e1'; });
      inputSection.appendChild(input);
      w.appendChild(inputSection);

      // Live cursive preview area
      const previewArea = document.createElement('div');
      previewArea.style.cssText =
        'min-height:80px;display:flex;align-items:center;justify-content:center;padding:16px;border-bottom:1px solid #e2e8f0;';

      const previewText = document.createElement('div');
      previewText.style.cssText =
        "font-family:'Dancing Script',cursive;font-weight:700;font-size:32px;color:#1e3a5f;font-style:italic;text-align:center;word-break:break-word;user-select:none;";
      previewText.textContent = input.value || '\u00A0';
      previewArea.appendChild(previewText);
      w.appendChild(previewArea);

      // Live-update preview as user types
      input.addEventListener('input', () => {
        previewText.textContent = input.value || '\u00A0';
      });

      // Signee label
      const signeeRow = document.createElement('div');
      signeeRow.style.cssText = 'padding:8px 16px;font-size:13px;color:#64748b;border-bottom:1px solid #e2e8f0;';
      const signeeLabel = document.createElement('span');
      signeeLabel.textContent = t('Signee:') + ' ';
      signeeLabel.style.fontWeight = '500';
      const signeeName = document.createElement('span');
      signeeName.textContent = input.value || '';
      signeeName.style.color = '#334155';
      signeeRow.appendChild(signeeLabel);
      signeeRow.appendChild(signeeName);
      w.appendChild(signeeRow);

      input.addEventListener('input', () => {
        signeeName.textContent = input.value || '';
      });

      // Button row: Cancel + Sign now
      const btnRow = document.createElement('div');
      btnRow.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;padding:10px 16px;';

      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.textContent = t('Cancel');
      cancelBtn.style.cssText =
        'padding:8px 20px;border:1px solid #cbd5e1;background:#fff;color:#475569;border-radius:6px;font-size:14px;font-weight:500;cursor:pointer;';
      cancelBtn.addEventListener('click', () => {
        input.value = shouldPopulateName ? (session?.guestName || '') : '';
        previewText.textContent = input.value || '\u00A0';
        signeeName.textContent = input.value || '';
      });

      const signBtn = document.createElement('button');
      signBtn.type = 'button';
      signBtn.textContent = t('Sign now');
      signBtn.style.cssText =
        'padding:8px 20px;border:none;background:#059669;color:#fff;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;';
      // Hover affordance (matches the green Confirm / Today buttons in this flow)
      signBtn.addEventListener('mouseenter', () => {
        if (!signBtn.disabled) signBtn.style.background = '#047857';
      });
      signBtn.addEventListener('mouseleave', () => {
        if (!signBtn.disabled) signBtn.style.background = '#059669';
      });

      signBtn.addEventListener('click', async () => {
        const name = input.value.trim();
        if (!name) {
          input.style.borderBottomColor = '#ef4444';
          input.focus();
          return;
        }

        signBtn.disabled = true;
        signBtn.textContent = t('Saving...');

        try {
          if (document.fonts && document.fonts.ready) {
            await document.fonts.ready;
          }

          const dataUrl = renderTypedSignatureToCanvas(name);

          await apiClient.patch('/agreements/progress', {
            fieldId,
            value: dataUrl,
            fieldType: 'typed-signature',
          });

          setTypedSignatures((prev) => ({ ...prev, [fieldId]: dataUrl }));
          renderTypedSignatureLocked(w, dataUrl);
        } catch (err) {
          signBtn.disabled = false;
          signBtn.textContent = t('Sign now');
        }
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); signBtn.click(); }
      });

      btnRow.appendChild(cancelBtn);
      btnRow.appendChild(signBtn);
      w.appendChild(btnRow);
    });
  };

  // Render typed signature text onto a canvas and return a data URL.
  // The canvas is sized TIGHTLY around the glyphs (not to the on-screen box
  // width) so the baked image's aspect ratio matches the text. The
  // signing PDF renders the signature in a fixed 320x80 box via object-fit:contain;
  // baking box-width whitespace made wide on-screen boxes produce a tiny PDF
  // signature (e.g. an 8:1 image squeezed into a 4:1 box). A tight image fills the
  // box height at any width, so it looks identically large in the UI and the PDF.
  const renderTypedSignatureToCanvas = (name: string): string => {
    const dpr = Math.max(window.devicePixelRatio || 1, 2);
    const slant = -0.18;
    const fontSize = 48;
    const padX = 28;
    const padY = 14;
    const font = `700 ${fontSize}px 'Dancing Script', cursive`;

    // Measure the rendered text so the canvas hugs the glyphs (plus a little
    // padding and room for the italic shear) instead of the box width.
    const measureCtx = document.createElement('canvas').getContext('2d')!;
    measureCtx.font = font;
    const textWidth = measureCtx.measureText(name).width;
    const skewExtra = Math.abs(slant) * fontSize;
    const width = Math.ceil(textWidth + skewExtra + padX * 2);
    const height = Math.ceil(fontSize * 1.35 + padY * 2);

    const canvas = document.createElement('canvas');
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.font = font;
    ctx.fillStyle = '#1e3a5f';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    // Apply italic slant via canvas transform (Dancing Script has no native italic)
    ctx.save();
    ctx.transform(1, 0, slant, 1, 0, 0);
    ctx.fillText(name, width / 2, height / 2);
    ctx.restore();

    return canvas.toDataURL('image/png');
  };

  // Render locked typed signature display
  const renderTypedSignatureLocked = (wrapper: HTMLElement, dataUrl: string) => {
    wrapper.innerHTML = '';
    wrapper.style.position = 'relative';
    wrapper.style.border = '2px solid #d1fae5';
    wrapper.style.background = '#f0fdf4';

    const img = document.createElement('img');
    img.src = dataUrl;
    img.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;';
    wrapper.appendChild(img);

    const badge = document.createElement('div');
    badge.style.cssText =
      'position:absolute;top:6px;right:6px;background:#059669;color:white;padding:3px 8px;border-radius:4px;font-size:11px;font-weight:600;';
    badge.textContent = '\u2713 ' + t('Signed');
    wrapper.appendChild(badge);
  };

  // Store resize handlers for cleanup
  const resizeHandlersRef = useRef<(() => void)[]>([]);

  // Scan DOM for field IDs after page injection
  const scanPageForFields = () => {
    const sigIds = Array.from(document.querySelectorAll('[data-signature-id]'))
      .map((el) => el.getAttribute('data-signature-id'))
      .filter(Boolean) as string[];
    const initIds = Array.from(document.querySelectorAll('[data-initials-id]'))
      .map((el) => el.getAttribute('data-initials-id'))
      .filter(Boolean) as string[];
    const inputIds = Array.from(document.querySelectorAll('[data-input-id]'))
      .map((el) => el.getAttribute('data-input-id'))
      .filter(Boolean) as string[];

    setKnownSigIds((prev) => new Set([...prev, ...sigIds]));
    setKnownInitIds((prev) => new Set([...prev, ...initIds]));
    setKnownInputIds((prev) => new Set([...prev, ...inputIds]));
  };

  // Set up interactive elements when page content changes
  useEffect(() => {
    // Guard: don't run until session is loaded
    if (!session) return;

    // Clean up previous resize handlers
    resizeHandlersRef.current.forEach((handler) => {
      window.removeEventListener('resize', handler);
    });
    resizeHandlersRef.current = [];

    // Set runtime attribute for signature pad components
    document.body.setAttribute('data-runtime', 'signer');

    // Set up signature confirm handler (called after signature is already saved by the component)
    (window as any).onSignatureConfirm = async ({ signatureId, image, width, height }: any) => {
      // Update local React state to reflect the saved signature
      setSignatures((prev) => ({ ...prev, [signatureId]: image }));
      // Capture signature dimensions for PDF consistency
      if (width && height) {
        setSigDims((prev) => ({ ...prev, [signatureId]: { w: width, h: height } }));
      }
      console.log('📝 Signature confirmed in React state:', signatureId, { width, height });
    };

    // Load signature_pad library and initialize pads
    const scriptId = 'signature-pad-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://cdn.jsdelivr.net/npm/signature_pad@5/dist/signature_pad.umd.min.js';
      script.async = true;
      document.body.appendChild(script);
    }

    // Track setup timers so stale ones can be cancelled on cleanup. A leftover
    // timer firing after completion (or after a newer init) would re-run with
    // stale isCompletedSession/typedSignatures and could restore the editable
    // "Sign now" UI on a completed agreement or wipe a locked signature image.
    const setupTimers: number[] = [];

    const onScriptLoad = () => {
      // Schedule after React injects the page HTML
      setupTimers.push(
        window.setTimeout(() => {
          setupInteractiveElements();
          initializeSignaturePads();
          initializeTypedSignatures();
          scanPageForFields(); // Scan for field IDs after DOM setup
        }, 0)
      ); // Microtask to run after HTML injection
    };

    script.addEventListener('load', onScriptLoad);

    // If script is already loaded, run immediately
    // @ts-ignore
    if (window.SignaturePad) {
      onScriptLoad();
    } else {
      // Even if signature pad isn't loaded, set up interactive elements
      setupTimers.push(
        window.setTimeout(() => {
          setupInteractiveElements();
          initializeTypedSignatures();
          scanPageForFields(); // Scan for field IDs
        }, 0)
      ); // Microtask to run after HTML injection
    }

    return () => {
      script.removeEventListener('load', onScriptLoad);
      setupTimers.forEach((id) => clearTimeout(id));
      resizeHandlersRef.current.forEach((handler) => window.removeEventListener('resize', handler));
      stopSelfiePoll();
    };
  }, [session, currentPageIndex, signatures, typedSignatures, initials, checkboxValues, numberValues, multiSelectValues, completed, guestInfoCounts]);

  // Add message listener for selfie uploads (must be before early returns)
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      // More flexible origin check for iframe communication
      if (e.origin !== window.location.origin && !e.origin.includes(window.location.hostname)) {
        return;
      }

      if (e.data?.type === 'besty:selfieUploaded') {
        const pendingImageId = pendingGuestSelfieRef.current;

        if (pendingImageId) {
          // Guest-info selfie — route to customImages instead of global selfieUrl
          const url = String(e.data.url);
          setCustomImages((prev) => ({
            ...prev,
            [pendingImageId]: { url, status: 'completed', fileName: null, error: null },
          }));
          setCustomImageRefreshTrigger((prev) => prev + 1);
          pendingGuestSelfieRef.current = null;
        } else {
          // Standard selfie (template-level)
          setSelfieStatus('completed');
          setSelfieUrl(e.data.url);
        }

        setSelfieModalOpen(false); // close modal
        setSelfieCaptureToken(null);
        stopSelfiePoll();
        (document.activeElement as HTMLElement | null)?.blur?.();

        // Render standard selfie inline immediately (only when not a guest-info selfie)
        if (!pendingImageId) {
          document.querySelectorAll('[data-selfie-id]').forEach((placeholder) => {
            const el = placeholder as HTMLElement;
            el.style.cssText = `${el.style.cssText}; max-width: 100%; box-sizing: border-box;`;

            const retakeDisabled = isCompletedSession;
            el.innerHTML = `
              <div style="text-align: center; padding: 12px; max-width:100%; box-sizing:border-box;">
                <img src="${e.data.url}" style="max-width:min(420px, 100%);max-height:420px;width:100%;object-fit:cover;border-radius:6px;border:1px solid #d1d5db" />
                <button type="button" data-selfie-retake
                  ${retakeDisabled ? 'disabled aria-disabled="true"' : ''}
                  style="margin-top:10px;background:${retakeDisabled ? '#9ca3af' : '#6b7280'};color:#fff;border:none;border-radius:6px;padding:8px 12px;cursor:${retakeDisabled ? 'not-allowed' : 'pointer'};font-size:.875rem;display:block;margin-left:auto;margin-right:auto;">
                  Retake selfie
                </button>
              </div>
            `;
            if (!retakeDisabled) {
              placeholder.querySelector<HTMLButtonElement>('[data-selfie-retake]')?.addEventListener('click', (ev) => {
                ev.preventDefault();
                startSelfieCapture();
              });
            }
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleInputSave = async (inputId: string, value: string) => {
    try {
      if (isCompletedSession) return;

      if (!session) {
        console.log('⚠️ Skipping input save - session not loaded yet');
        return;
      }

      if (!['sent', 'in_progress'].includes(session.status)) {
        console.log('⚠️ Skipping input save - session status:', session.status);
        return;
      }

      setInputs((prev) => ({ ...prev, [inputId]: value }));

      // Save to backend via API
      await apiClient.patch(`/agreements/progress`, {
        fieldId: inputId,
        value: value,
        fieldType: 'input',
      });
    } catch (error) {
      console.error('Error saving input:', error);
    }
  };

  const handleInitials = (initialsId: string) => {
    if (isCompletedSession) return;
    setInitialsModalId(initialsId);
    setInitialsModalValue('');
    setInitialsModalOpen(true);
  };

  const submitInitials = async () => {
    if (!initialsModalId || !initialsModalValue.trim()) return;
    const fieldId = initialsModalId;
    const trimmedInitials = initialsModalValue.trim().toUpperCase();

    // Update the UI immediately with the completed state
    const initialsBox = document.querySelector(`[data-initials-id="${fieldId}"]`) as HTMLElement;
    if (initialsBox) {
      initialsBox.dataset.initialsValue = trimmedInitials;
      initialsBox.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 60px;
          padding: 12px 16px;
          background: #f0fdf4;
          border: 2px solid #059669;
          border-radius: 10px;
          gap: 8px;
        ">
          <span style="
            font-size: 24px;
            font-weight: 700;
            font-family: 'Times New Roman', Georgia, serif;
            font-style: italic;
            color: #1a1a1a;
            letter-spacing: 1px;
          ">${trimmedInitials}</span>
          <span style="
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            background: #059669;
            border-radius: 50%;
            color: white;
            font-size: 14px;
          ">✓</span>
        </div>
      `;
      initialsBox.style.pointerEvents = 'none';
      initialsBox.style.cursor = 'default';
    }

    // Update state
    setInitials((prev) => ({ ...prev, [fieldId]: trimmedInitials }));

    // Close modal
    setInitialsModalOpen(false);
    setInitialsModalId(null);
    setInitialsModalValue('');

    // Save to backend via API
    try {
      await apiClient.patch(`/agreements/progress`, {
        fieldId,
        value: trimmedInitials,
        fieldType: 'initials',
      });
      console.log('✅ Initials saved:', fieldId, trimmedInitials);
    } catch (error) {
      console.error('Error saving initials:', error);
    }
  };

  const setupInteractiveElements = () => {
    // Note: Signature pad components handle their own clicks via the script in the component definition
    // Note: Initials are now handled by mountInitialsUI() for better styling

    // Handle input fields
    const inputBoxes = document.querySelectorAll('[data-input-id]');
    inputBoxes.forEach((box) => {
      const inputId = box.getAttribute('data-input-id');
      if (!inputId) return;

      const inputField = box.querySelector('input,textarea') as HTMLInputElement | HTMLTextAreaElement;
      if (!inputField) return;

      const boxEl = box as HTMLElement;

      // Override editor's inline styles to make inputs full-width in frontend
      // Editor uses inline-block/resize for editing, but we want full-width for guests
      if (!boxEl.dataset.stylesOverridden) {
        boxEl.dataset.stylesOverridden = 'true';
        boxEl.style.display = 'block';
        boxEl.style.width = '100%';
        boxEl.style.minWidth = 'unset';
        boxEl.style.resize = 'none';
        boxEl.style.overflow = 'visible';
      }

      // Apply placeholder. Prefer the server-translated placeholder from the
      // session payload (the data-placeholder attribute stays in the template's
      // source language since Azure doesn't translate custom data-* attributes),
      // falling back to the raw attribute.
      const sessionPlaceholder = session?.inputFields?.find((f) => f.id === inputId)?.placeholder;
      const customPlaceholder = sessionPlaceholder || boxEl.getAttribute('data-placeholder');
      if (customPlaceholder && !boxEl.dataset.placeholderApplied) {
        boxEl.dataset.placeholderApplied = 'true';
        inputField.placeholder = decodeHtml(customPlaceholder);
      }

      // Check if this is a date input
      const isDateInput = inputField instanceof HTMLInputElement && inputField.type === 'date';

      // Add * indicator if this is a required field
      const isRequired = isFieldRequired(inputId, 'input');
      if (isRequired && !boxEl.dataset.requiredIndicatorAdded && !isDateInput) {
        boxEl.dataset.requiredIndicatorAdded = 'true';
        // For text/textarea inputs, append * to placeholder
        if (inputField.placeholder && !inputField.placeholder.includes('*')) {
          inputField.placeholder = inputField.placeholder + ' *';
        } else if (!inputField.placeholder) {
          inputField.placeholder = t('Enter text') + ' *';
        }
      }

      // Check if input was already saved and restore value
      const savedInput = inputs[inputId];
      if (savedInput) {
        inputField.value = savedInput;
        console.log('🔄 Rehydrated saved input:', inputId, savedInput);
      }

      // Track last saved value on the element to avoid stale closure issues
      (inputField as any)._lastSavedValue = inputs[inputId] ?? '';

      // Lock text/date inputs when agreement is completed
      if (isCompletedSession) {
        inputField.disabled = true;
        inputField.style.backgroundColor = '#f9fafb';
        inputField.style.cursor = 'not-allowed';

        // Also disable flatpickr's visible altInput for already-initialized date fields
        if (boxEl.dataset.flatpickrInitialized) {
          boxEl.querySelectorAll('input').forEach((inp) => {
            (inp as HTMLInputElement).disabled = true;
            (inp as HTMLInputElement).style.backgroundColor = '#f9fafb';
            (inp as HTMLInputElement).style.cursor = 'not-allowed';
          });
        }

        return; // skip attaching save listeners and flatpickr
      }

      const saveInput = async () => {
        const value = inputField.value.trim();
        const lastSaved = (inputField as any)._lastSavedValue ?? '';
        if (value !== lastSaved) {
          (inputField as any)._lastSavedValue = value;
          await handleInputSave(inputId, value);
        }
      };

      // Initialize flatpickr for date inputs (modern date picker)
      if (isDateInput && !boxEl.dataset.flatpickrInitialized) {
        boxEl.dataset.flatpickrInitialized = 'true';

        // Change input type to text so flatpickr can take over
        (inputField as HTMLInputElement).type = 'text';
        inputField.placeholder = isRequired ? t('Select a date') + ' *' : t('Select a date');
        inputField.style.cursor = 'pointer';

        flatpickr(inputField, {
          dateFormat: 'Y-m-d',
          altInput: true,
          altFormat: 'F j, Y', // Display format: "December 11, 2025"
          allowInput: true,
          disableMobile: true, // Use flatpickr on mobile too
          // Anchor the calendar to the field's own wrapper instead of body.
          // Default (body-append + absolute document coords) mis-positioned the
          // calendar to the far right of the viewport on wide screens; `static`
          // pins it directly under the input at any viewport width.
          static: true,
          defaultDate: savedInput || undefined,
          onChange: (_selectedDates: Date[], dateStr: string) => {
            (inputField as any)._lastSavedValue = dateStr;
            handleInputSave(inputId, dateStr);
          },
          onReady: (
            _selectedDates: Date[],
            _dateStr: string,
            instance: {
              calendarContainer: HTMLElement;
              altInput?: HTMLInputElement;
              setDate: (date: Date, triggerChange: boolean) => void;
              close: () => void;
            }
          ) => {
            // Style the altInput with calendar icon and asterisk
            if (instance.altInput) {
              // Set placeholder with asterisk if required
              instance.altInput.placeholder = isRequired ? t('Select a date') + ' *' : t('Select a date');

              // Make boxEl position relative for absolute positioning of asterisk
              boxEl.style.position = 'relative';

              // Wrap the altInput in a container for positioning the calendar icon
              const wrapper = document.createElement('div');
              wrapper.style.cssText = 'position: relative; display: block; width: 100%;';

              instance.altInput.parentNode?.insertBefore(wrapper, instance.altInput);
              wrapper.appendChild(instance.altInput);

              // In `static` mode flatpickr wraps the field in a `.flatpickr-wrapper`
              // that defaults to inline-block, which collapses the field to its
              // placeholder width. Force it full-width so the date field matches
              // the other inputs (scoped to this instance, not a global rule).
              const fpWrapper = wrapper.parentElement;
              if (fpWrapper && fpWrapper.classList.contains('flatpickr-wrapper')) {
                fpWrapper.style.display = 'block';
                fpWrapper.style.width = '100%';
              }

              // Style the input to make room for the calendar icon
              instance.altInput.style.width = '100%';
              instance.altInput.style.paddingRight = '32px';
              instance.altInput.style.boxSizing = 'border-box';

              // Add calendar icon
              const calendarIcon = document.createElement('span');
              calendarIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #64748b;"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`;
              calendarIcon.style.cssText =
                'position: absolute; right: 8px; top: 50%; transform: translateY(-50%); pointer-events: none;';
              wrapper.appendChild(calendarIcon);

              // Add asterisk at top-right OUTSIDE the container if required
              if (isRequired) {
                const asteriskSpan = document.createElement('span');
                asteriskSpan.style.cssText =
                  'color: #dc2626; font-weight: bold; position: absolute; top: -8px; right: -12px; font-size: 14px; z-index: 1;';
                asteriskSpan.textContent = '*';
                boxEl.appendChild(asteriskSpan);
              }
            }

            // Add "Today" button at the bottom of the calendar
            const todayBtn = document.createElement('button');
            todayBtn.type = 'button';
            todayBtn.textContent = t('Today');
            todayBtn.style.cssText = `
              width: 100%;
              padding: 8px;
              background: #059669;
              color: white;
              border: none;
              border-radius: 0 0 4px 4px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 500;
            `;
            todayBtn.addEventListener('click', () => {
              instance.setDate(new Date(), true);
              instance.close();
            });
            todayBtn.addEventListener('mouseenter', () => {
              todayBtn.style.background = '#047857';
            });
            todayBtn.addEventListener('mouseleave', () => {
              todayBtn.style.background = '#059669';
            });
            instance.calendarContainer.appendChild(todayBtn);
          },
        });
      } else if (!isDateInput && !boxEl.dataset.flatpickrInitialized) {
        // For non-date inputs (and not already-initialized flatpickr date inputs), use blur/input event listeners
        // Guard against flatpickrInitialized to prevent duplicate listeners on date inputs
        // (after flatpickr init, inputField.type changes from 'date' to 'text')
        if (!boxEl.dataset.listenersAttached) {
          boxEl.dataset.listenersAttached = 'true';
          inputField.addEventListener('blur', saveInput);
          inputField.addEventListener('input', () => {
            // Debounced save on input (save after 1 second of no typing)
            clearTimeout((inputField as any)._saveTimeout);
            (inputField as any)._saveTimeout = setTimeout(saveInput, 1000);
          });
        }
      }
    });

    // Handle checkbox components
    const checkboxBoxes = document.querySelectorAll('[data-checkbox-id]');
    checkboxBoxes.forEach((box) => {
      const checkboxId = box.getAttribute('data-checkbox-id');
      if (!checkboxId) return;

      const boxEl = box as HTMLElement;

      // Lock already-mounted checkboxes when agreement is completed
      if (boxEl.dataset.checkboxMounted === 'true') {
        if (isCompletedSession) {
          const inp = boxEl.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
          if (inp && !inp.disabled) {
            inp.disabled = true;
            inp.style.cursor = 'not-allowed';
            // Also disable the label click
            const label = boxEl.querySelector('label') as HTMLLabelElement | null;
            if (label) label.style.cursor = 'not-allowed';
          }
        }
        return;
      }
      boxEl.dataset.checkboxMounted = 'true';

      const sessionCheckbox = session?.checkboxes?.find((c) => c.id === checkboxId);
      const domRequired = boxEl.getAttribute('data-required');
      const isRequired = sessionCheckbox?.required ?? (domRequired !== null ? domRequired === 'true' : true);
      const domLabel = boxEl.getAttribute('data-label');
      const label = escapeHtml(decodeHtml(sessionCheckbox?.label || domLabel || 'Checkbox'));
      const isChecked = checkboxValues[checkboxId] === true;
      const isDisabled = isCompletedSession;

      // Override the box content with a nice styled checkbox
      boxEl.style.margin = '10px 0';
      boxEl.style.padding = '12px';
      boxEl.style.background = isChecked ? '#f0fdf4' : '#ffffff';
      boxEl.style.border = `2px solid ${isChecked ? '#059669' : '#cbd5e1'}`;
      boxEl.style.borderRadius = '8px';
      boxEl.style.transition = 'all 0.2s ease';

      boxEl.innerHTML = `
        <label style="
          display: flex;
          align-items: flex-start;
          gap: 12px;
          cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
          font: inherit;
          color: inherit;
        ">
          <input type="checkbox"
            data-checkbox-input="${checkboxId}"
            ${isChecked ? 'checked' : ''}
            ${isDisabled ? 'disabled' : ''}
            style="
              width: 20px;
              height: 20px;
              margin-top: 2px;
              cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
              accent-color: #059669;
            "
          />
          <span>${label}${isRequired ? ' <span style="color: #dc2626;">*</span>' : ''}</span>
        </label>
      `;

      // Add change listener
      const checkbox = boxEl.querySelector(`input[data-checkbox-input="${checkboxId}"]`) as HTMLInputElement;
      if (checkbox && !isDisabled) {
        checkbox.addEventListener('change', async () => {
          const checked = checkbox.checked;
          setCheckboxValues((prev) => ({ ...prev, [checkboxId]: checked }));

          // Update styling
          boxEl.style.background = checked ? '#f0fdf4' : '#ffffff';
          boxEl.style.border = `2px solid ${checked ? '#059669' : '#cbd5e1'}`;

          // Save to server
          try {
            await apiClient.patch(`/agreements/progress`, {
              fieldId: checkboxId,
              value: checked,
              fieldType: 'checkbox',
            });
          } catch (err) {
            console.error('Failed to save checkbox:', err);
          }
        });
      }
    });

    // Handle number input components
    const numberBoxes = document.querySelectorAll('[data-number-id]');
    numberBoxes.forEach((box) => {
      const numberId = box.getAttribute('data-number-id');
      if (!numberId) return;

      const boxEl = box as HTMLElement;

      // Lock already-mounted number inputs when agreement is completed
      if (boxEl.dataset.numberMounted === 'true') {
        if (isCompletedSession) {
          const inp = boxEl.querySelector('input[type="number"]') as HTMLInputElement | null;
          if (inp && !inp.disabled) {
            inp.disabled = true;
            inp.style.background = '#f9fafb';
            inp.style.cursor = 'not-allowed';
          }
        }
        return;
      }
      boxEl.dataset.numberMounted = 'true';

      const numberField = session?.numberInputs?.find((n) => n.id === numberId);
      const isRequired = numberField?.required ?? true;
      const label = escapeHtml(decodeHtml(numberField?.label || ''));
      const min = escapeHtml(numberField?.min || '');
      const max = escapeHtml(numberField?.max || '');
      const savedValue = numberValues[numberId] || '';
      const isDisabled = isCompletedSession;

      // Override the box content with a styled number input
      boxEl.style.margin = '10px 0';

      boxEl.innerHTML = `
        <div style="display: flex; flex-direction: column; gap: 6px;">
          ${
            label
              ? `<label style="
            font: inherit;
            color: inherit;
          ">${label}${isRequired ? ' <span style="color: #dc2626;">*</span>' : ''}</label>`
              : ''
          }
          <input type="number"
            data-number-input="${numberId}"
            value="${escapeHtml(savedValue)}"
            ${min ? `min="${min}"` : ''}
            ${max ? `max="${max}"` : ''}
            ${isDisabled ? 'disabled' : ''}
            placeholder="${escapeHtml(isRequired && !label ? t('Enter a number') + ' *' : t('Enter a number'))}"
            style="
              width: 180px;
              padding: 10px 12px;
              border: 2px solid #cbd5e1;
              border-radius: 6px;
              font-size: inherit;
              font-family: inherit;
              transition: border-color 0.2s ease;
              ${isDisabled ? 'background: #f9fafb; cursor: not-allowed;' : ''}
            "
          />
        </div>
      `;

      // Add change listener
      const numberInput = boxEl.querySelector(`input[data-number-input="${numberId}"]`) as HTMLInputElement;
      if (numberInput && !isDisabled) {
        const saveNumber = async () => {
          const value = numberInput.value;
          setNumberValues((prev) => ({ ...prev, [numberId]: value }));

          // Validate and update styling
          const numVal = Number(value);
          const isValid = value !== '' && (!min || numVal >= Number(min)) && (!max || numVal <= Number(max));
          numberInput.style.borderColor = value && !isValid ? '#dc2626' : '#cbd5e1';

          // Save to server
          try {
            await apiClient.patch(`/agreements/progress`, {
              fieldId: numberId,
              value: value,
              fieldType: 'number',
            });
          } catch (err) {
            console.error('Failed to save number input:', err);
          }
        };

        numberInput.addEventListener('blur', saveNumber);
        numberInput.addEventListener('input', () => {
          clearTimeout((numberInput as any)._saveTimeout);
          (numberInput as any)._saveTimeout = setTimeout(saveNumber, 1000);
        });
      }
    });

    // Handle multi-select components
    const multiSelectBoxes = document.querySelectorAll('[data-multiselect-id]');
    multiSelectBoxes.forEach((box) => {
      const multiSelectId = box.getAttribute('data-multiselect-id');
      if (!multiSelectId) return;

      const boxEl = box as HTMLElement;

      // Lock already-mounted multi-selects when agreement is completed
      if (boxEl.dataset.multiSelectMounted === 'true') {
        if (isCompletedSession) {
          boxEl.querySelectorAll('input').forEach((inp) => {
            const el = inp as HTMLInputElement;
            el.disabled = true;
            el.style.cursor = 'not-allowed';
          });
        }
        return;
      }
      boxEl.dataset.multiSelectMounted = 'true';

      const field = session?.multiSelects?.find((m) => m.id === multiSelectId);
      // Fall back to the element's own data-options when the session metadata is
      // missing/empty, so a required field never renders with zero choices.
      const parseAttrOptions = (): string[] => {
        const raw = boxEl.getAttribute('data-options');
        if (!raw) return [];
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed.map((o) => String(o).trim()).filter(Boolean);
        } catch {
          return raw
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
        }
        return [];
      };
      const displayOptions = field?.options?.length ? field.options : parseAttrOptions();
      // Stored/submitted values use the source-language option so the signed PDF
      // and validation stay language-agnostic; the guest still sees translated text.
      const sourceOptions = field?.originalOptions?.length ? field.originalOptions : displayOptions;
      const label = escapeHtml(decodeHtml(field?.label || boxEl.getAttribute('data-label') || 'Select all that apply'));
      const isRequired = field?.required ?? boxEl.getAttribute('data-required') === 'true';
      const isMultiple = field?.multiple ?? boxEl.getAttribute('data-multiple') !== 'false';
      const inline = boxEl.getAttribute('data-inline') === 'true';
      // A min greater than zero forces selections even when Required is off, so
      // mark the field required in the UI whenever it effectively mandates a
      // selection. Keeps the asterisk consistent with completion/finalize.
      const attrMinRaw = boxEl.getAttribute('data-min');
      const attrMin = attrMinRaw && Number.isFinite(Number(attrMinRaw)) ? Number(attrMinRaw) : 0;
      const minSelections = !isMultiple
        ? isRequired
          ? 1
          : 0
        : field
          ? multiSelectBounds(field).min
          : isRequired
            ? Math.max(1, attrMin)
            : attrMin;
      const showRequiredMark = isRequired || minSelections > 0;
      const selected = multiSelectValues[multiSelectId] || [];
      const isDisabled = isCompletedSession;

      // Render borderless so the field flows with the agreement text instead of
      // reading as a floating widget. The component's saved style attribute still
      // carries the editor placeholder's card styling (border, padding, tinted
      // background, centered text, min-height), so clear all of it here; the
      // guest portal only ever showed a box because those were left in place.
      boxEl.style.margin = '16px 0';
      boxEl.style.padding = '0';
      boxEl.style.background = 'transparent';
      boxEl.style.border = 'none';
      boxEl.style.borderRadius = '0';
      boxEl.style.textAlign = 'left';
      boxEl.style.minHeight = '0';

      const optionsHtml = displayOptions
        .map((optText, i) => {
          const value = sourceOptions[i] ?? optText;
          const isChecked = selected.includes(value);
          return `
            <label style="display:flex;align-items:center;gap:8px;cursor:${isDisabled ? 'not-allowed' : 'pointer'};font: inherit;color: inherit;">
              <input
                type="${isMultiple ? 'checkbox' : 'radio'}"
                name="ms-${escapeHtml(multiSelectId)}"
                data-ms-input="${escapeHtml(multiSelectId)}"
                value="${escapeHtml(value)}"
                ${isChecked ? 'checked' : ''}
                ${isDisabled ? 'disabled' : ''}
                style="width:18px;height:18px;cursor:${isDisabled ? 'not-allowed' : 'pointer'};accent-color:#059669;"
              />
              <span>${escapeHtml(decodeHtml(optText))}</span>
            </label>`;
        })
        .join('');

      // Each option label hugs its content width (align-items: flex-start) so
      // only the checkbox/radio and its text are clickable; empty space to the
      // right of a short option, in line with the row, no longer toggles it.
      boxEl.innerHTML = `
        <div style="display:flex;flex-direction:column;gap:8px;">
          <div style="font-weight:500;color:inherit;">${label}${showRequiredMark ? ' <span style="color:#dc2626;">*</span>' : ''}</div>
          <div style="display:flex;flex-direction:${inline ? 'row' : 'column'};flex-wrap:wrap;align-items:${inline ? 'center' : 'flex-start'};gap:${inline ? '16px' : '8px'};">
            ${optionsHtml}
          </div>
        </div>
      `;

      if (!isDisabled) {
        const inputs = Array.from(
          boxEl.querySelectorAll(`input[data-ms-input="${multiSelectId}"]`)
        ) as HTMLInputElement[];
        // Cap on selections for multiple-choice fields, so the guest cannot
        // check past the configured max (single-choice is inherently capped at
        // one). Falls back to the element's data-max when session metadata is
        // absent, mirroring the other attribute fallbacks above.
        const attrMaxRaw = boxEl.getAttribute('data-max');
        const attrMax =
          attrMaxRaw && attrMaxRaw !== '' && Number.isFinite(Number(attrMaxRaw)) ? Number(attrMaxRaw) : null;
        const maxSelections = !isMultiple ? 1 : field ? multiSelectBounds(field).max : attrMax;
        inputs.forEach((input) => {
          // Single-choice fields render as radios, which the browser cannot
          // natively deselect. For an OPTIONAL field the guest must be able to
          // return to an empty selection, so re-clicking the checked radio clears
          // it (and fires change to persist the empty value). Required radios are
          // left non-clearable so a guest cannot wipe a mandatory answer and get
          // silently blocked at completion.
          if (!isMultiple && !isRequired) {
            input.addEventListener('mousedown', () => {
              input.dataset.wasChecked = input.checked ? 'true' : 'false';
            });
            input.addEventListener('click', () => {
              if (input.dataset.wasChecked === 'true') {
                input.checked = false;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          }
          input.addEventListener('change', async () => {
            // Enforce the max cap for multiple-choice: if this change pushed the
            // selection past the max, revert it and skip saving so the guest
            // cannot exceed the limit (rather than being silently blocked at
            // completion time with no hint).
            if (isMultiple && input.checked && maxSelections !== null) {
              const distinctChecked = new Set(inputs.filter((i) => i.checked).map((i) => i.value));
              if (distinctChecked.size > maxSelections) {
                input.checked = false;
                return;
              }
            }
            const checkedValues = inputs.filter((i) => i.checked).map((i) => i.value);
            setMultiSelectValues((prev) => ({ ...prev, [multiSelectId]: checkedValues }));
            try {
              await apiClient.patch(`/agreements/progress`, {
                fieldId: multiSelectId,
                value: checkedValues,
                fieldType: 'multi-select',
              });
            } catch (err) {
              console.error('Failed to save multi-select:', err);
            }
          });
        });
      }
    });
  };

  const isGuestInfoComplete = () => {
    if (!session?.guestInfoComponents || session.guestInfoComponents.length === 0) return true;
    return session.guestInfoComponents.every((comp) => isGuestInfoComponentComplete(comp));
  };

  // Multi-select completion mirrors the backend finalize rule (min/max, single-choice
  // handling, and dedupe + drop of options no longer configured). The rule lives in
  // @besty/shared so the client completion gate and the server 400 can never disagree
  // (which would show the guest "complete" then reject the submit).
  const areMultiSelectsComplete = () => {
    const fields = session?.multiSelects || [];
    return fields.every((field) => {
      const allowed = field.originalOptions?.length ? field.originalOptions : field.options;
      return isMultiSelectComplete(field, multiSelectValues[field.id], allowed);
    });
  };

  // BES-9331: value resolution for the shared completion gate. Progress-channel
  // values come from the ref mirror (always latest, even inside stale closures
  // like handleComplete); upload-channel values from the formUploads state.
  const getFormComponentValue = (field: ExtractedFormComponent): unknown =>
    getFormComponentDescriptor(field.type)?.valueChannel === 'upload'
      ? formUploads[field.id]?.status === 'completed'
        ? formUploads[field.id]?.record
        : undefined
      : formComponentValuesRef.current[field.id] ?? formComponentValues[field.id];

  // Completion mirrors the backend finalize rule exactly: both call
  // findIncompleteFormComponents in @besty/shared, so the client gate and the
  // server 400 can never disagree.
  const areFormComponentsComplete = () =>
    findIncompleteFormComponents(session?.formComponents || [], getFormComponentValue).length === 0;

  const areAllRequiredFieldsComplete = () => {
    if (!session) return false;

    // Use signatureFields with required status if available, otherwise fall back to treating all as required
    const requiredSigIds = session.signatureFields
      ? session.signatureFields.filter((f) => f.required).map((f) => f.id)
      : session.signatureIds && session.signatureIds.length > 0
        ? session.signatureIds
        : Array.from(knownSigIds);

    const requiredInitIds = session.initialFields
      ? session.initialFields.filter((f) => f.required).map((f) => f.id)
      : session.initialIds && session.initialIds.length > 0
        ? session.initialIds
        : Array.from(knownInitIds);

    const requiredInputIds = session.inputFields
      ? session.inputFields.filter((f) => f.required).map((f) => f.id)
      : session.inputIds !== undefined && session.inputIds.length > 0
        ? session.inputIds
        : Array.from(knownInputIds);

    const allSignatures = requiredSigIds.every((id) => signatures[id]);
    const allInitials = requiredInitIds.every((id) => initials[id]);
    const allInputs = requiredInputIds.every((id) => inputs[id] && inputs[id].trim() !== '');

    // Check required typed signatures
    const requiredTypedSigIds = (session.typedSignatureFields || []).filter((f) => f.required).map((f) => f.id);
    const allTypedSignatures = requiredTypedSigIds.every((id) => typedSignatures[id]);

    // Check required checkboxes
    const requiredCheckboxes = session.checkboxes?.filter((c) => c.required) || [];
    const allCheckboxes = requiredCheckboxes.every((c) => checkboxValues[c.id] === true);

    // Check required number inputs
    const requiredNumbers = session.numberInputs?.filter((n) => n.required) || [];
    const allNumbers = requiredNumbers.every((n) => {
      const val = numberValues[n.id];
      if (val === undefined || val === '') return false;
      const numVal = Number(val);
      if (n.min && numVal < Number(n.min)) return false;
      if (n.max && numVal > Number(n.max)) return false;
      return true;
    });

    // Check multi-select fields
    const allMultiSelects = areMultiSelectsComplete();

    // Check registry form components (shared gate, BES-9331)
    const allFormComponents = areFormComponentsComplete();

    // Check guest info fields (reuse isGuestInfoComplete)
    const allGuestInfo = isGuestInfoComplete();

    console.log('📋 Completion status:', {
      signatures: `${Object.keys(signatures).length}/${requiredSigIds.length} required`,
      typedSignatures: `${Object.keys(typedSignatures).length}/${requiredTypedSigIds.length} required`,
      initials: `${Object.keys(initials).length}/${requiredInitIds.length} required`,
      inputs: `${Object.keys(inputs).length}/${requiredInputIds.length} required`,
      checkboxes: `${Object.keys(checkboxValues).filter((k) => checkboxValues[k]).length}/${requiredCheckboxes.length} required`,
      numbers: `${Object.keys(numberValues).filter((k) => numberValues[k]).length}/${requiredNumbers.length} required`,
      multiSelects: allMultiSelects,
      formComponents: allFormComponents,
      guestInfo: allGuestInfo,
      allComplete:
        allSignatures && allTypedSignatures && allInitials && allInputs && allCheckboxes && allNumbers && allMultiSelects && allFormComponents && allGuestInfo,
    });

    return (
      allSignatures && allTypedSignatures && allInitials && allInputs && allCheckboxes && allNumbers && allMultiSelects && allFormComponents && allGuestInfo
    );
  };

  // Helper to check only signatures and initials completion (for UI display)
  const areSignaturesAndInitialsComplete = () => {
    if (!session) return false;

    const requiredSigIds = session.signatureFields
      ? session.signatureFields.filter((f) => f.required).map((f) => f.id)
      : session.signatureIds && session.signatureIds.length > 0
        ? session.signatureIds
        : Array.from(knownSigIds);

    const requiredInitIds = session.initialFields
      ? session.initialFields.filter((f) => f.required).map((f) => f.id)
      : session.initialIds && session.initialIds.length > 0
        ? session.initialIds
        : Array.from(knownInitIds);

    const allSignatures = requiredSigIds.every((id) => signatures[id]);
    const allInitials = requiredInitIds.every((id) => initials[id]);
    const requiredTypedSigIds = (session.typedSignatureFields || []).filter((f) => f.required).map((f) => f.id);
    const allTypedSignatures = requiredTypedSigIds.every((id) => typedSignatures[id]);

    return allSignatures && allInitials && allTypedSignatures;
  };

  // Helper to check only inputs completion (text, checkboxes, numbers) for UI display
  const areInputFieldsComplete = () => {
    if (!session) return false;

    const requiredInputIds = session.inputFields
      ? session.inputFields.filter((f) => f.required).map((f) => f.id)
      : session.inputIds !== undefined && session.inputIds.length > 0
        ? session.inputIds
        : Array.from(knownInputIds);

    const allInputs = requiredInputIds.every((id) => inputs[id] && inputs[id].trim() !== '');

    const requiredCheckboxes = session.checkboxes?.filter((c) => c.required) || [];
    const allCheckboxes = requiredCheckboxes.every((c) => checkboxValues[c.id] === true);

    const requiredNumbers = session.numberInputs?.filter((n) => n.required) || [];
    const allNumbers = requiredNumbers.every((n) => {
      const val = numberValues[n.id];
      if (val === undefined || val === '') return false;
      const numVal = Number(val);
      if (n.min && numVal < Number(n.min)) return false;
      if (n.max && numVal > Number(n.max)) return false;
      return true;
    });

    const allMultiSelects = areMultiSelectsComplete();

    return allInputs && allCheckboxes && allNumbers && allMultiSelects && areFormComponentsComplete();
  };

  const isSelfieCompleted = () => {
    return !session?.selfieRequired || selfieStatus === 'completed' || !!selfieUrl;
  };

  const isIdCompleted = () => {
    return !session?.idRequired || idStatus === 'completed';
  };

  const areRequiredCustomImagesCompleted = () => {
    if (!session?.imageUploads || session.imageUploads.length === 0) return true;

    // Check all required custom images are uploaded
    return session.imageUploads
      .filter((img) => img.required)
      .every((img) => customImages[img.id]?.status === 'completed');
  };

  const isGuestInfoComponentComplete = (comp: GuestInfoComponent) => {
    const count = resolveGuestCount(comp);
    for (let g = 0; g < count; g++) {
      for (let fi = 0; fi < comp.fields.length; fi++) {
        const field = comp.fields[fi];
        if (!field.required) continue;
        if (field.type === 'signature') {
          if (!signatures[`gi-${comp.id}-g${g}-sig-${fi}`]) return false;
        } else if (field.type === 'initials') {
          if (!initials[`gi-${comp.id}-g${g}-init-${fi}`]) return false;
        } else if (field.type === 'checkbox') {
          if (checkboxValues[`gi-${comp.id}-g${g}-chk-${fi}`] !== true) return false;
        } else if (field.type === 'image_upload') {
          if (customImages[`gi-${comp.id}-g${g}-img-${fi}`]?.status !== 'completed') return false;
        } else if (field.type === 'selfie') {
          if (customImages[`gi-${comp.id}-g${g}-selfie-${fi}`]?.status !== 'completed') return false;
        } else if (field.type === 'id_upload') {
          if (customImages[`gi-${comp.id}-g${g}-idupload-${fi}`]?.status !== 'completed') return false;
        } else {
          const fieldId = `gi-${comp.id}-g${g}-${field.type}-${fi}`;
          if (!guestInfoValues[fieldId] || !guestInfoValues[fieldId].trim()) return false;
        }
      }
    }
    return true;
  };

  const GUEST_FIELD_LABELS: Record<string, string> = {
    signature: 'Signature',
    initials: 'Initials',
    checkbox: 'Checkbox',
    image_upload: 'Photo Upload',
    selfie: 'Selfie',
    id_upload: 'ID Upload',
    text: 'Text Field',
    date: 'Date',
    email: 'Email',
    phone: 'Phone',
  };

  const getGuestInfoMissingFieldLabels = (comp: GuestInfoComponent): string[] => {
    const labels = new Set<string>();
    const count = resolveGuestCount(comp);
    for (let g = 0; g < count; g++) {
      for (let fi = 0; fi < comp.fields.length; fi++) {
        const field = comp.fields[fi];
        if (!field.required) continue;
        let isMissing = false;
        if (field.type === 'signature') {
          isMissing = !signatures[`gi-${comp.id}-g${g}-sig-${fi}`];
        } else if (field.type === 'initials') {
          isMissing = !initials[`gi-${comp.id}-g${g}-init-${fi}`];
        } else if (field.type === 'checkbox') {
          isMissing = checkboxValues[`gi-${comp.id}-g${g}-chk-${fi}`] !== true;
        } else if (field.type === 'image_upload') {
          isMissing = customImages[`gi-${comp.id}-g${g}-img-${fi}`]?.status !== 'completed';
        } else if (field.type === 'selfie') {
          isMissing = customImages[`gi-${comp.id}-g${g}-selfie-${fi}`]?.status !== 'completed';
        } else if (field.type === 'id_upload') {
          isMissing = customImages[`gi-${comp.id}-g${g}-idupload-${fi}`]?.status !== 'completed';
        } else {
          const fieldId = `gi-${comp.id}-g${g}-${field.type}-${fi}`;
          isMissing = !guestInfoValues[fieldId] || !guestInfoValues[fieldId].trim();
        }
        if (isMissing) {
          labels.add(GUEST_FIELD_LABELS[field.type] || field.label || 'Required Field');
        }
      }
    }
    return Array.from(labels);
  };

  // Return page indices with their missing field labels.
  // Used to drive the "earlier pages have gaps" hint when user advances past unfilled work.
  const getMissingRequiredPages = (): { page: number; fieldLabels: string[] }[] => {
    if (!session) return [];
    const { fieldIdToPage, fieldIdToLabel, selfiePage, idPage } = pageLocationRef.current;
    const pageFields = new Map<number, Set<string>>();

    const addField = (id: string | undefined | null, label?: string) => {
      if (!id) return;
      const idx = fieldIdToPage.get(id);
      if (idx === undefined) return;
      if (!pageFields.has(idx)) pageFields.set(idx, new Set());
      pageFields.get(idx)!.add(label || fieldIdToLabel.get(id) || 'Required Field');
    };

    const addPage = (pageIdx: number, label: string) => {
      if (!pageFields.has(pageIdx)) pageFields.set(pageIdx, new Set());
      pageFields.get(pageIdx)!.add(label);
    };

    // Use signatureFields/initialFields/inputFields when available; fall back to legacy ID arrays
    const requiredSigIds = session.signatureFields
      ? session.signatureFields.filter((f) => f.required).map((f) => f.id)
      : session.signatureIds && session.signatureIds.length > 0
        ? session.signatureIds
        : Array.from(knownSigIds);
    const requiredInitIds = session.initialFields
      ? session.initialFields.filter((f) => f.required).map((f) => f.id)
      : session.initialIds && session.initialIds.length > 0
        ? session.initialIds
        : Array.from(knownInitIds);
    const requiredInputIds = session.inputFields
      ? session.inputFields.filter((f) => f.required).map((f) => f.id)
      : session.inputIds !== undefined && session.inputIds.length > 0
        ? session.inputIds
        : Array.from(knownInputIds);

    requiredSigIds.forEach((id) => {
      if (!signatures[id]) addField(id, 'Signature');
    });
    (session.typedSignatureFields || []).forEach((f) => {
      if (f.required && !typedSignatures[f.id]) addField(f.id, 'Typed Signature');
    });
    requiredInitIds.forEach((id) => {
      if (!initials[id]) addField(id, 'Initials');
    });
    requiredInputIds.forEach((id) => {
      if (!inputs[id] || !inputs[id].trim()) addField(id);
    });
    (session.checkboxes || []).forEach((c) => {
      if (c.required && checkboxValues[c.id] !== true) addField(c.id, c.label || 'Checkbox');
    });
    (session.numberInputs || []).forEach((n) => {
      if (!n.required) return;
      const val = numberValues[n.id];
      const numVal = Number(val);
      const ok =
        val !== undefined &&
        val !== '' &&
        (!n.min || numVal >= Number(n.min)) &&
        (!n.max || numVal <= Number(n.max));
      if (!ok) addField(n.id, n.label || 'Number');
    });
    (session.multiSelects || []).forEach((m) => {
      // Matches completion gating and finalize (dedupe + drop stale options, then min/max).
      const allowed = m.originalOptions?.length ? m.originalOptions : m.options;
      if (!isMultiSelectComplete(m, multiSelectValues[m.id], allowed)) {
        addField(m.id, m.label || 'Multi-select');
      }
    });
    (session.imageUploads || []).forEach((img) => {
      if (img.required && customImages[img.id]?.status !== 'completed') addField(img.id, 'Photo Upload');
    });
    // Registry form components: the shared gate returns the failing fields with
    // their (server-translated) labels.
    findIncompleteFormComponents(session.formComponents || [], getFormComponentValue).forEach(({ field }) => {
      addField(field.id, field.label || 'Required Field');
    });
    (session.guestInfoComponents || []).forEach((comp) => {
      if (isGuestInfoComponentComplete(comp)) return;
      const idx = fieldIdToPage.get(comp.id);
      if (idx === undefined) return;
      const missingLabels = getGuestInfoMissingFieldLabels(comp);
      if (missingLabels.length > 0) {
        missingLabels.forEach((label) => addPage(idx, label));
      } else {
        addPage(idx, 'Guest Information');
      }
    });
    if (session.selfieRequired && !isSelfieCompleted() && selfiePage !== null) addPage(selfiePage, 'Selfie');
    if (session.idRequired && !isIdCompleted() && idPage !== null) addPage(idPage, 'ID Upload');

    return Array.from(pageFields.entries())
      .map(([page, labels]) => ({ page, fieldLabels: Array.from(labels) }))
      .sort((a, b) => a.page - b.page);
  };

  const isCompletedSession = completed || session?.status === 'completed';

  const serializePdfPayload = () => {
    try {
      // Use original English pages for PDF generation (not translated display pages)
      const pdfPages = originalPagesRef.current || session?.pages;
      if (!pdfPages) return null;

      const contentWidth = (document.querySelector('.agreement-injected') as HTMLElement | null)?.clientWidth || 800;

      function injectValues(html: string) {
        let out = html.replace(/contenteditable="[^"]*"/g, '');

        // Signatures: keep wrapper, replace inner content only, use captured dimensions
        // Skip guest-info signatures (gi-*) — already rendered via live DOM capture
        Object.entries(signatures).forEach(([id, img]) => {
          if (id.startsWith('gi-')) return;
          const dims = sigDims[id];
          const sized = dims
            ? `<div style="width:${dims.w}px;height:${dims.h}px;box-sizing:border-box;border:1px dashed #cbd5e1;border-radius:6px;display:block;overflow:hidden">
                 <img src="${img}" style="width:100%;height:100%;object-fit:contain;display:block" />
               </div>`
            : `<div style="width:100%;height:100%;box-sizing:border-box;border:1px dashed #cbd5e1;border-radius:6px;display:block;overflow:hidden">
                 <img src="${img}" style="width:100%;height:100%;object-fit:contain;display:block" />
               </div>`;
          const re = new RegExp(`(<div[^>]*data-signature-id=["']${id}["'][^>]*>)[\\s\\S]*?(</div>)`, 'g');
          out = out.replace(re, `$1${sized}$2`);
        });

        // Typed signatures: keep wrapper, replace inner content with rendered image
        Object.entries(typedSignatures).forEach(([id, img]) => {
          const re = new RegExp(`(<div[^>]*data-typed-signature-id=["']${id}["'][^>]*>)[\\s\\S]*?(</div>)`, 'g');
          out = out.replace(
            re,
            `$1<div style="width:100%;height:100%;box-sizing:border-box;border:1px dashed #cbd5e1;border-radius:6px;display:block;overflow:hidden">
                 <img src="${img}" style="width:100%;height:100%;object-fit:contain;display:block" />
               </div>$2`
          );
        });

        // Initials: keep wrapper
        // Skip guest-info initials (gi-*) — already rendered via live DOM capture
        Object.entries(initials).forEach(([id, text]) => {
          if (id.startsWith('gi-')) return;
          const re = new RegExp(`(<[^>]*data-initials-id=["']${id}["'][^>]*>)[\\s\\S]*?(</[^>]+>)`, 'g');
          out = out.replace(re, `$1<span style="font-weight:600;color:#059669;font-family:serif;">${text} ✓</span>$2`);
        });

        // Helper to format date values nicely (YYYY-MM-DD → "December 11, 2025")
        const formatDateValue = (value: string): string => {
          const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (dateMatch) {
            const [, year, month, day] = dateMatch;
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
          }
          return value;
        };

        // inputs → block text (format dates nicely)
        Object.entries(inputs).forEach(([id, val]) => {
          const re = new RegExp(`<[^>]*data-input-id=["']${id}["'][\\s\\S]*?<\\/[^>]+>`, 'g');
          const displayValue = formatDateValue(String(val ?? '').trim());
          out = out.replace(
            re,
            `<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;">${escapeHtml(displayValue)}</div>`
          );
        });

        // Checkboxes: replace ALL checkboxes with styled checkbox indicator
        // Iterate over session.checkboxes (not just checkboxValues) so that checkboxes
        // the guest never interacted with still get their correct label from data-label,
        // instead of retaining the stale default "I agree to the terms and conditions" HTML.
        (session?.checkboxes || []).forEach((checkboxField) => {
          const id = checkboxField.id;
          const checked = checkboxValues[id] === true;
          const label = escapeHtml(decodeHtml(checkboxField.originalLabel || checkboxField.label || ''));
          // Use </div> specifically to match the outer wrapper (not nested </span> or </label>)
          const re = new RegExp(`<div[^>]*data-checkbox-id=["']${id}["'][^>]*>[\\s\\S]*?</div>`, 'g');
          const checkboxIcon = checked
            ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;background:#059669;border-radius:4px;color:white;font-size:14px;font-weight:bold;">✓</span>`
            : `<span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;background:#f1f5f9;border:2px solid #cbd5e1;border-radius:4px;"></span>`;
          out = out.replace(
            re,
            `<div style="display:flex;align-items:flex-start;gap:12px;padding:12px 16px;background:${checked ? '#f0fdf4' : '#ffffff'};border:2px solid ${checked ? '#059669' : '#e2e8f0'};border-radius:8px;font-size:14px;margin:8px 0;">
              ${checkboxIcon}
              <span style="flex:1;padding-top:1px;">${label}</span>
            </div>`
          );
        });

        // Number inputs: replace with the entered value
        // Note: number inputs have nested divs, so we match greedily to the last </div>
        // within the component boundary (before next data-* attribute or end)
        Object.entries(numberValues).forEach(([id, val]) => {
          const numberField = session?.numberInputs?.find((n) => n.id === id);
          const label = escapeHtml(decodeHtml(numberField?.originalLabel || numberField?.label || ''));
          const re = new RegExp(`<div[^>]*data-number-id=["']${id}["'][^>]*>(?:[\\s\\S]*?</div>){2}`, 'g');
          const displayValue = escapeHtml(val !== '' ? val : '—');
          out = out.replace(
            re,
            label
              ? `<div style="font-size:14px;">
                  <span>${label}: </span>
                  <span style="font-weight:600;border:1px solid #d1d5db;border-radius:4px;padding:4px 8px;">${displayValue}</span>
                </div>`
              : `<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;font-weight:600;">${displayValue}</div>`
          );
        });

        // Multi-selects: replace the placeholder wrapper with the label and a
        // checked/unchecked list of the options. Uses source-language label and
        // options so the signed PDF stays in one consistent language even when the
        // guest UI was translated. Balanced div matching mirrors guest-info because
        // the editor placeholder contains nested divs.
        (session?.multiSelects || []).forEach((field) => {
          const selectedValues = multiSelectValues[field.id] || [];
          const displayOptions = field.originalOptions?.length ? field.originalOptions : field.options;
          // Source-language label/options so the signed PDF stays in one consistent
          // language even when the guest UI was translated.
          const inner = renderMultiSelectPdfHtml({
            label: field.originalLabel || field.label || '',
            options: displayOptions,
            selected: selectedValues,
          });

          // Replace every wrapper carrying this id (a stale template may still contain
          // duplicate ids). findEnclosingBalancedDiv bounds each wrapper quote-aware so a
          // '>' or a '<div'/'</div>' substring inside data-label/data-options can't
          // truncate it.
          const startTag = `data-multiselect-id="${field.id}"`;
          const altStartTag = `data-multiselect-id='${field.id}'`;
          let searchFrom = 0;
          while (searchFrom < out.length) {
            const dq = out.indexOf(startTag, searchFrom);
            const sq = out.indexOf(altStartTag, searchFrom);
            const startIdx = dq === -1 ? sq : sq === -1 ? dq : Math.min(dq, sq);
            if (startIdx === -1) break;
            const bounds = findEnclosingBalancedDiv(out, startIdx);
            if (!bounds) {
              searchFrom = startIdx + 1;
              continue;
            }
            out = out.substring(0, bounds.start) + inner + out.substring(bounds.end);
            searchFrom = bounds.start + inner.length;
          }
        });

        // BES-9331: registry form components. renderPdfHtml lives in
        // @besty/shared so this and the server-side fallback render the same
        // markup (source-language label via originalLabel). Balanced-div
        // matching because the editor placeholder contains nested divs.
        (session?.formComponents || []).forEach((field) => {
          const descriptor = getFormComponentDescriptor(field.type);
          if (!descriptor) return;
          const inner = descriptor.renderPdfHtml(field, getFormComponentValue(field));
          const startTag = `data-form-component-id="${field.id}"`;
          const altStartTag = `data-form-component-id='${field.id}'`;
          let searchFrom = 0;
          while (searchFrom < out.length) {
            const dq = out.indexOf(startTag, searchFrom);
            const sq = out.indexOf(altStartTag, searchFrom);
            const startIdx = dq === -1 ? sq : sq === -1 ? dq : Math.min(dq, sq);
            if (startIdx === -1) break;
            const bounds = findEnclosingBalancedDiv(out, startIdx);
            if (!bounds) {
              searchFrom = startIdx + 1;
              continue;
            }
            out = out.substring(0, bounds.start) + inner + out.substring(bounds.end);
            searchFrom = bounds.start + inner.length;
          }
        });

        // Selfie/ID: use live state; keep wrapper
        if (selfieUrl) {
          out = out.replace(
            /(<div[^>]*data-selfie-id=(["'])[^"']+\2[^>]*>)[\s\S]*?(<\/div>)/g,
            `$1<img src="${selfieUrl}" style="width:100%;height:auto;max-height:100%;object-fit:cover;display:block;border-radius:6px;border:1px solid #d1d5db" />$3`
          );
        }
        if (idUrl) {
          out = out.replace(
            /(<div[^>]*data-id-id=(["'])[^"']+\2[^>]*>)[\s\S]*?(<\/div>)/g,
            `$1<img src="${idUrl}" style="width:100%;height:auto;max-height:100%;object-fit:cover;display:block;border-radius:6px;border:1px solid #d1d5db" />$3`
          );
        }

        // Helper: reconstruct guest info HTML from React state (used when DOM element is not on the current page)
        const buildGuestInfoFromState = (comp: GuestInfoComponent): string => {
          const guestCount = resolveGuestCount(comp);

          if (guestCount === 0) {
            return '<div style="text-align:center;padding:16px;color:#64748b;font-size:14px;">Add guest information…</div>';
          }

          let html = '';
          for (let n = 0; n < guestCount; n++) {
            html +=
              '<div style="border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;margin:12px 0;background:#fff;">';
            // Header
            html += `<div style="background:#f0f9ff;border-bottom:1px solid #e2e8f0;padding:10px 16px;"><span style="font-weight:600;font-size:14px;color:#1e293b;">${escapeHtml(comp.label || 'Guest')} ${n + 1}</span></div>`;
            // Fields
            html += '<div style="padding:16px;display:flex;flex-direction:column;gap:12px;">';
            comp.fields.forEach((field, fieldIndex) => {
              const labelText =
                field.label || field.type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());

              if (field.type === 'signature') {
                const sigFieldId = `gi-${comp.id}-g${n}-sig-${fieldIndex}`;
                const sigData = signatures[sigFieldId];
                html += `<div><label style="display:block;font-size:13px;color:#64748b;margin-bottom:4px;">${escapeHtml(labelText)}</label>`;
                if (sigData) {
                  html += `<div style="width:100%;max-width:320px;min-height:120px;height:160px;border:1px dashed #cbd5e1;border-radius:6px;overflow:hidden;"><img src="${sigData}" style="width:100%;height:100%;object-fit:contain;display:block" /></div>`;
                } else {
                  html +=
                    '<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;min-height:20px;color:#94a3b8;">—</div>';
                }
                html += '</div>';
              } else if (field.type === 'initials') {
                const initFieldId = `gi-${comp.id}-g${n}-init-${fieldIndex}`;
                const initData = initials[initFieldId];
                html += `<div><label style="display:block;font-size:13px;color:#64748b;margin-bottom:4px;">${escapeHtml(labelText)}</label>`;
                if (initData) {
                  html += `<span style="font-weight:600;color:#059669;font-family:serif;">${escapeHtml(String(initData))} ✓</span>`;
                } else {
                  html +=
                    '<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;min-height:20px;color:#94a3b8;">—</div>';
                }
                html += '</div>';
              } else if (field.type === 'selfie' || field.type === 'id_upload' || field.type === 'image_upload') {
                const idSuffix = field.type === 'selfie' ? 'selfie' : field.type === 'id_upload' ? 'idupload' : 'img';
                const imgFieldId = `gi-${comp.id}-g${n}-${idSuffix}-${fieldIndex}`;
                const imgData = customImages[imgFieldId];
                html += `<div><label style="display:block;font-size:13px;color:#64748b;margin-bottom:4px;">${escapeHtml(labelText)}</label>`;
                if (imgData?.status === 'completed' && imgData.url) {
                  html += `<img src="${imgData.url}" style="width:100%;height:auto;max-height:400px;object-fit:cover;display:block;border-radius:6px;border:1px solid #d1d5db" />`;
                } else {
                  html +=
                    '<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;min-height:20px;color:#94a3b8;">—</div>';
                }
                html += '</div>';
              } else if (field.type === 'checkbox') {
                const chkFieldId = `gi-${comp.id}-g${n}-chk-${fieldIndex}`;
                const isChecked = checkboxValues[chkFieldId] === true;
                const checkIcon = isChecked
                  ? '<span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;background:#059669;border-radius:4px;color:white;font-size:14px;font-weight:bold;">✓</span>'
                  : '<span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;background:#f1f5f9;border:2px solid #cbd5e1;border-radius:4px;"></span>';
                html += `<div style="display:flex;align-items:flex-start;gap:12px;padding:12px 16px;background:${isChecked ? '#f0fdf4' : '#ffffff'};border:2px solid ${isChecked ? '#059669' : '#e2e8f0'};border-radius:8px;font-size:14px;margin:8px 0;">${checkIcon}<span style="flex:1;padding-top:1px;">${escapeHtml(field.label || '')}</span></div>`;
              } else {
                // text, date, email, phone
                const fieldId = `gi-${comp.id}-g${n}-${field.type}-${fieldIndex}`;
                const val = guestInfoValues[fieldId] || '';
                const displayVal = val ? escapeHtml(val) : '—';
                html += `<div><label style="display:block;font-size:13px;color:#64748b;margin-bottom:4px;">${escapeHtml(labelText)}</label>`;
                html += `<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;min-height:20px;">${displayVal}</div></div>`;
              }
            });
            html += '</div></div>';
          }
          return html;
        };

        // Guest info: capture live DOM content if on current page, otherwise reconstruct from React state
        (session?.guestInfoComponents || []).forEach((comp) => {
          const liveEl = document.querySelector(`[data-guest-info-id="${comp.id}"]`);
          if (liveEl) {
            // Sync input/textarea .value to HTML attribute so innerHTML captures typed values
            liveEl.querySelectorAll('input, textarea, select').forEach((inp) => {
              const el = inp as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
              el.setAttribute('value', el.value);
              if (el.tagName === 'TEXTAREA') el.textContent = el.value;
              if (el.tagName === 'SELECT') {
                el.querySelectorAll('option').forEach((opt) => {
                  if ((opt as HTMLOptionElement).value === el.value) opt.setAttribute('selected', 'selected');
                  else opt.removeAttribute('selected');
                });
              }
            });
          }
          // If the guest info component is not on the currently visible page, reconstruct from React state
          let liveHtml = liveEl?.innerHTML || buildGuestInfoFromState(comp);
          // Strip interactive elements that shouldn't appear in the PDF
          liveHtml = liveHtml
            .replace(/<button[^>]*>[\s\S]*?<\/button>/gi, '') // all buttons
            .replace(/<canvas[^>]*>[\s\S]*?<\/canvas>/gi, '') // signature canvases
            .replace(/<input[^>]*type="file"[^>]*>/gi, '') // hidden file inputs only
            .replace(/<div[^>]*class="sig-toolbar"[^>]*>[\s\S]*?<\/div>/gi, '') // signature toolbar
            // Replace text/email/tel/date inputs with styled static text
            .replace(/<input[^>]*value="([^"]*)"[^>]*>/gi, (_match, val) => {
              const decoded = val ? escapeHtml(decodeHtml(val)) : '—';
              return `<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;min-height:20px;">${decoded}</div>`;
            })
            .replace(/<input[^>]*>/gi, '') // remove any remaining inputs without value
            // Replace textareas with styled static text
            .replace(/<textarea[^>]*>([\s\S]*?)<\/textarea>/gi, (_match, val) => {
              const decoded = val ? escapeHtml(decodeHtml(val.trim())) : '—';
              return `<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;min-height:20px;">${decoded}</div>`;
            })
            // Replace selects with styled static text showing selected value
            .replace(
              /<select[^>]*>[\s\S]*?<option[^>]*selected="selected"[^>]*>([^<]*)<\/option>[\s\S]*?<\/select>/gi,
              (_match, val) => {
                const decoded = val ? escapeHtml(val.trim()) : '—';
                return `<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;">${decoded}</div>`;
              }
            )
            .replace(
              /<select[^>]*>[\s\S]*?<\/select>/gi,
              '<div style="border:1px solid #d1d5db;border-radius:4px;padding:6px;font-size:14px;">—</div>'
            ); // fallback for selects without selection
          // Replace guest-info placeholder using balanced div matching
          const startTag = `data-guest-info-id="${comp.id}"`;
          const altStartTag = `data-guest-info-id='${comp.id}'`;
          const startIdx = out.indexOf(startTag) !== -1 ? out.indexOf(startTag) : out.indexOf(altStartTag);
          if (startIdx !== -1) {
            const divStart = out.lastIndexOf('<div', startIdx);
            if (divStart !== -1) {
              let depth = 0;
              let i = divStart;
              let endIdx = -1;
              while (i < out.length) {
                if (out.substring(i, i + 4) === '<div') {
                  depth++;
                  i += 4;
                } else if (out.substring(i, i + 6) === '</div>') {
                  depth--;
                  if (depth === 0) {
                    endIdx = i + 6;
                    break;
                  }
                  i += 6;
                } else {
                  i++;
                }
              }
              if (endIdx !== -1) {
                out = out.substring(0, divStart) + `<div>${liveHtml}</div>` + out.substring(endIdx);
              }
            }
          }
        });

        // Custom images: replace each uploaded image (skip guest-info images — already in live DOM capture)
        const guestInfoImageIds = new Set<string>();
        (session?.guestInfoComponents || []).forEach((comp) => {
          const count = resolveGuestCount(comp);
          for (let g = 0; g < count; g++) {
            comp.fields.forEach((field, fi) => {
              if (field.type === 'selfie') guestInfoImageIds.add(`gi-${comp.id}-g${g}-selfie-${fi}`);
              if (field.type === 'id_upload') guestInfoImageIds.add(`gi-${comp.id}-g${g}-idupload-${fi}`);
              if (field.type === 'image_upload') guestInfoImageIds.add(`gi-${comp.id}-g${g}-img-${fi}`);
            });
          }
        });
        Object.entries(customImages).forEach(([imageId, imageData]) => {
          if (guestInfoImageIds.has(imageId)) return; // Already rendered via live DOM capture
          if (imageData.status === 'completed' && imageData.url) {
            const imageRe = new RegExp(`(<div[^>]*data-image-id=(["'])${imageId}\\2[^>]*>)[\\s\\S]*?(<\\/div>)`, 'g');
            out = out.replace(
              imageRe,
              `$1<img src="${imageData.url}" style="width:100%;height:auto;max-height:100%;object-fit:cover;display:block;border-radius:6px;border:1px solid #d1d5db" />$3`
            );
          }
        });

        return out;
      }

      const pages = pdfPages.map((p) => ({
        html: injectValues(p.html || ''),
        css: p.css || '',
      }));

      return { widthPx: contentWidth, pages };
    } catch (error) {
      console.warn('Failed to serialize PDF payload, falling back to server-side generation:', error);
      return null;
    }
  };

  const pollVerificationStatus = async (attemptCount = 0) => {
    const MAX_POLL_ATTEMPTS = 20; // Poll for ~1 minute max (20 attempts × 3 seconds)

    if (attemptCount >= MAX_POLL_ATTEMPTS) {
      setVerificationError('Verification is taking longer than expected. Please close this tab and try again.');
      setVerifying(false);
      return;
    }

    try {
      const result = (await apiClient.post('/agreements/session/complete-verification', {})) as any;

      // Update attempt count from backend whenever returned
      if (result.verificationAttempts !== undefined) {
        setVerificationAttempts(result.verificationAttempts);
      }

      if (!result.success) {
        if (result.ageRestricted) {
          setVerificationError(
            `Age requirement not met. You must be at least ${result.minimumAge} years old. You are ${result.age}.`
          );
          setVerifying(false);
          return;
        } else if (result.error?.includes('processing')) {
          console.log(`Verification still processing... attempt ${attemptCount + 1}/${MAX_POLL_ATTEMPTS}`);
          verificationPollRef.current = window.setTimeout(() => {
            pollVerificationStatus(attemptCount + 1);
          }, 3000);
          return;
        } else {
          setVerificationError(
            result.error || 'Identity verification failed. Please try again with a valid government ID.'
          );
          setVerifying(false);
          return;
        }
      }

      // Success!
      setVerificationCompleted(true);
      setVerificationError(null);
      setVerifying(false);
    } catch (error: any) {
      setVerificationError(error.message || 'Verification failed. Please try again.');
      setVerifying(false);
    }
  };

  const handleVerifyIdentity = async () => {
    // Clear any existing polling
    if (verificationPollRef.current) {
      clearTimeout(verificationPollRef.current);
      verificationPollRef.current = null;
    }

    setVerifying(true);
    setVerificationError(null);

    try {
      // Start verification (get client secret)
      const result = (await apiClient.post('/agreements/session/start-verification', {})) as any;

      if (result.maxAttemptsReached) {
        setVerificationError('Maximum verification attempts reached. Please contact your host.');
        setVerificationAttempts(MAX_VERIFICATION_ATTEMPTS);
        setVerifying(false);
        return;
      }

      // Stripe already verified this session (e.g. the guest left the page
      // before the completion poll finished). Verified sessions have no client
      // secret, so skip the Stripe modal and sync our record directly.
      if (result.alreadyVerified) {
        await pollVerificationStatus(0);
        return;
      }

      const { clientSecret } = result;
      if (!clientSecret) {
        throw new Error(t('Verification could not be started. Please close this tab and try again.'));
      }

      // Load Stripe SDK
      const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

      if (!stripePublishableKey) {
        throw new Error('Stripe publishable key not configured.');
      }

      const stripe = await loadStripe(stripePublishableKey);
      if (!stripe) {
        throw new Error('Failed to load Stripe SDK');
      }

      // Launch Stripe verification modal
      const { error } = await stripe.verifyIdentity(clientSecret);

      if (error) {
        setVerificationError(error.message || 'Verification failed');
        setVerifying(false);
        return;
      }

      // User completed Stripe flow - now check backend
      // This will start polling if status is 'processing'
      await pollVerificationStatus(0);
    } catch (error: any) {
      // Check if it's a max attempts error
      if (error?.response?.data?.maxAttemptsReached) {
        setVerificationError('Maximum verification attempts reached. Please contact your host.');
        setVerificationAttempts(MAX_VERIFICATION_ATTEMPTS);
      } else {
        setVerificationError(error.message || 'Verification failed. Please try again.');
      }
      setVerifying(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (verificationPollRef.current) {
        clearTimeout(verificationPollRef.current);
      }
    };
  }, []);

  const handleComplete = async () => {
    if (!session || isCompletedSession || completing) return;

    setCompleting(true);
    try {
      await flushInputSaves();

      // Serialize the current page HTML for PDF generation
      const pdfPayload = serializePdfPayload();
      console.log(' Serialized PDF payload:', pdfPayload ? `${pdfPayload.pages.length} pages` : 'not available');

      const result = (await apiClient.post(`/agreements/session/finalize`, {
        signatures,
        typedSignatures,
        initials,
        inputs,
        checkboxes: checkboxValues,
        numberInputs: numberValues,
        multiSelects: multiSelectValues,
        // BES-9331: progress-channel form component values (attachments are
        // validated server-side from progress_json.formUploads). Read from the
        // ref so values flushed a moment ago are included despite this closure.
        formComponents: { ...formComponentValues, ...formComponentValuesRef.current },
        selfieStatus,
        pdfPayload,
        guestInfoValues,
        guestInfoCounts,
      })) as any;

      console.log('Agreement completed:', result);
      setCompleted(true);
      if (result?.pdfUrl) setPdfUrl(result.pdfUrl);
      void refetchPortal?.();
    } catch (error) {
      console.error('Error completing agreement:', error);
      alert(`${t('Failed to complete agreement')}: ${error instanceof Error ? error.message : t('Unknown error')}`);
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">{t('Loading rental agreement...')}</span>
      </div>
    );
  }

  if (error || !session) {
    return <Banner type="error">{error || t('The signing link is invalid or has expired.')}</Banner>;
  }

  // Helper to save any unsaved input changes before navigation
  const flushInputSaves = async () => {
    // Guard: only flush if session is loaded and in valid state
    if (!session || !['sent', 'in_progress'].includes(session.status)) {
      console.log('⚠️ Skipping flush - session not ready or completed');
      return;
    }

    const tasks: Promise<any>[] = [];
    document.querySelectorAll('[data-input-id]').forEach((box) => {
      const inputId = box.getAttribute('data-input-id');
      const inputEl = box.querySelector('input,textarea') as HTMLInputElement | HTMLTextAreaElement | null;
      if (!inputId || !inputEl) return;

      const current = inputEl.value.trim();
      const saved = inputs[inputId] ?? '';
      if (current !== saved) {
        // reuse existing save helper; guards are already in handleInputSave
        tasks.push(handleInputSave(inputId, current));
      }
    });
    // BES-9331: flush pending debounced form-component saves. Each flush
    // returns its progress PATCH promise (the ref mirror updates
    // synchronously), collected into tasks so navigation waits for the write
    // to land instead of racing a refresh.
    Object.values(formComponentFlushersRef.current).forEach((flush) => {
      const pending = flush();
      if (pending) tasks.push(pending);
    });
    await Promise.allSettled(tasks);
  };

  const nextPage = async () => {
    if (currentPageIndex < session.pages.length - 1) {
      await flushInputSaves();
      const nextIdx = currentPageIndex + 1;
      setCurrentPageIndex(nextIdx);
      setMaxVisitedPageIndex((p) => Math.max(p, nextIdx));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevPage = async () => {
    if (currentPageIndex > 0) {
      await flushInputSaves();
      setCurrentPageIndex(currentPageIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Compute completion state
  // Note: The max attempts check only applies when verification is required.
  // If verification is disabled on the template, we skip the attempts check entirely.
  const canComplete =
    isSelfieCompleted() &&
    isIdCompleted() &&
    areRequiredCustomImagesCompleted() &&
    areAllRequiredFieldsComplete() &&
    (!verificationRequired || verificationCompleted) &&
    (!verificationRequired || verificationAttempts < MAX_VERIFICATION_ATTEMPTS || verificationCompleted);

  // Build the bottom CTA - only show when not completed
  const bottomCta = !isCompletedSession ? (
    <div>
      {/* Stripe Identity Verification Section */}
      {verificationRequired && !verificationCompleted && (
        <div className="mb-4 pb-4 border-b border-slate-200">
          <div className="mb-3">
            <h4 className="font-semibold text-base text-slate-900">{t('Identity Verification Required')}</h4>
            {minimumAge && (
              <p className="text-sm text-slate-600 mt-1">
                {t('You must be at least {{minimumAge}} years old to sign this agreement.', { minimumAge })}
              </p>
            )}
          </div>

          {verificationAttempts >= MAX_VERIFICATION_ATTEMPTS ? (
            <div className="p-4 bg-amber-50 text-amber-900 rounded-lg text-sm border-l-4 border-amber-500">
              <div className="font-semibold mb-1">⚠️ {t('Maximum Attempts Reached')}</div>
              <div>
                {t(
                  'You have reached the maximum number of verification attempts ({{count}}). Please contact your host for assistance.',
                  { count: MAX_VERIFICATION_ATTEMPTS }
                )}
              </div>
            </div>
          ) : (
            <>
              {verificationError && !verificationError.includes('processing') && (
                <div className="mb-3 p-4 bg-red-50 text-red-800 rounded-lg text-sm border-l-4 border-red-500">
                  <div className="font-semibold mb-1">❌ {t('Verification Failed')}</div>
                  <div>{verificationError}</div>
                  {verificationAttempts > 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      {t('Attempts:')} {verificationAttempts}/{MAX_VERIFICATION_ATTEMPTS}
                    </div>
                  )}
                </div>
              )}

              {verifying && verificationError?.includes('processing') && (
                <div className="mb-3 p-4 bg-blue-50 text-blue-800 rounded-lg text-sm border-l-4 border-blue-500">
                  <div className="font-semibold mb-1">⏳ {t('Processing Verification')}</div>
                  <div>{t('Your verification is being processed. Please wait...')}</div>
                </div>
              )}

              <Button
                onClick={handleVerifyIdentity}
                disabled={verifying}
                variant="primary"
                size="md"
                className="rounded-lg"
              >
                {verifying ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="animate-spin inline-block h-4 w-4 rounded-full border-2 border-white border-b-transparent" />
                    {verificationError?.includes('processing') ? t('Processing...') : t('Verifying Identity…')}
                  </span>
                ) : verificationError ? (
                  t('Try Again')
                ) : (
                  <>
                    {t('Verify Identity')}
                    {minimumAge && <span className="ml-2 text-sm opacity-90">({minimumAge}+)</span>}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      )}

      {verificationRequired && verificationCompleted && (
        <div className="mb-4 pb-4 border-b border-slate-200">
          <div className="flex items-center gap-2 text-green-600 font-semibold">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            {t('Identity Verified')}
          </div>
        </div>
      )}

      <Button
        onClick={handleComplete}
        disabled={completing || !canComplete}
        variant="primary"
        size="lg"
        className="w-full rounded-lg h-14 text-base"
      >
        {completing ? (
          <span className="inline-flex items-center gap-2">
            <span className="animate-spin inline-block h-4 w-4 rounded-full border-2 border-white border-b-transparent" />
            {t('Submitting…')}
          </span>
        ) : (
          t('Complete Agreement')
        )}
      </Button>

      {/* Requirements checklist while incomplete */}
      {!canComplete && (
        <div className="mt-3 text-sm text-slate-600">
          <div className="font-medium mb-1">
            {t('Requirements for completion')}{' '}
            <span className="font-normal">
              ({t('marked with')} <span style={{ color: '#dc2626' }}>*</span>)
            </span>
            :
          </div>
          <ul className="list-disc ml-5 space-y-1">
            {session?.selfieRequired && (
              <li className={isSelfieCompleted() ? 'text-emerald-700' : 'text-slate-600'}>
                {isSelfieCompleted() ? '✓' : '•'} {t('Selfie capture')}
              </li>
            )}
            {session?.idRequired && (
              <li className={isIdCompleted() ? 'text-emerald-700' : 'text-slate-600'}>
                {isIdCompleted() ? '✓' : '•'} {t('ID document upload')}
              </li>
            )}
            {session?.imageUploads
              ?.filter((img) => img.required)
              .map((imageUpload) => {
                const isCompleted = customImages[imageUpload.id]?.status === 'completed';
                return (
                  <li key={imageUpload.id} className={isCompleted ? 'text-emerald-700' : 'text-slate-600'}>
                    {isCompleted ? '✓' : '•'} {t('Image upload')}
                  </li>
                );
              })}
            {((session?.signatureIds && session.signatureIds.length > 0) ||
              knownSigIds.size > 0 ||
              (session?.initialIds && session.initialIds.length > 0) ||
              knownInitIds.size > 0 ||
              (session?.typedSignatureFields && session.typedSignatureFields.some((f) => f.required))) && (
              <li className={areSignaturesAndInitialsComplete() ? 'text-emerald-700' : 'text-slate-600'}>
                {areSignaturesAndInitialsComplete() ? '✓' : '•'} {t('All required signatures/initials')}
              </li>
            )}
            {((session?.inputIds && session.inputIds.length > 0) ||
              knownInputIds.size > 0 ||
              (session?.checkboxes && session.checkboxes.some((c) => c.required)) ||
              (session?.numberInputs && session.numberInputs.some((n) => n.required)) ||
              (session?.multiSelects &&
                session.multiSelects.some((m) => {
                  // Show the line when the field is genuinely required (min > 0) OR is
                  // currently incomplete (e.g. an optional cap-only field left over max by
                  // restored progress). A satisfied optional field stays hidden.
                  const allowed = m.originalOptions?.length ? m.originalOptions : m.options;
                  return multiSelectBounds(m).min > 0 || !isMultiSelectComplete(m, multiSelectValues[m.id], allowed);
                })) ||
              // BES-9331: registry form components gate areInputFieldsComplete, so
              // they must also be able to surface this row (required components, or
              // an optional one holding an invalid value, block completion).
              (session?.formComponents && session.formComponents.some((f) => f.required)) ||
              !areFormComponentsComplete()) && (
              <li className={areInputFieldsComplete() ? 'text-emerald-700' : 'text-slate-600'}>
                {areInputFieldsComplete() ? '✓' : '•'} {t('All required inputs')}
              </li>
            )}
            {session?.guestInfoComponents && session.guestInfoComponents.length > 0 && (
              <li className={isGuestInfoComplete() ? 'text-emerald-700' : 'text-slate-600'}>
                {isGuestInfoComplete() ? '✓' : '•'} {t('All required guest information')}
              </li>
            )}
            {verificationRequired && (
              <li className={verificationCompleted ? 'text-emerald-700' : 'text-slate-600'}>
                {verificationCompleted ? '✓' : '•'} {t('Identity verification')}
                {minimumAge ? ` (${minimumAge}+)` : ''}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  ) : undefined; // No bottom CTA when completed

  return (
    <Layout
      showReservation
      title={session?.templateName || t('Rental Agreement')}
      hideTitle
      bottomCta={bottomCta}
      bottomCtaSticky={false}
    >
      <LanguageSelector />
      {isCompletedSession && (
        <Banner type="success">
          <div>
            {t('Agreement has been completed')}
            {pdfUrl && (
              <div className="mt-2 text-sm">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-800 underline font-medium"
                >
                  {t('Download agreement here')}
                </a>
              </div>
            )}
          </div>
        </Banner>
      )}

      {session && (
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Header with page counter + animated progress */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-100">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-slate-600">
                {t('Page {{current}} of {{total}}', { current: currentPageIndex + 1, total: session.pages.length })}
              </div>
              <div className="flex-1 h-1.5 bg-gray-200 rounded">
                <div
                  className="h-1.5 rounded"
                  style={{
                    width: `${((currentPageIndex + 1) / session.pages.length) * 100}%`,
                    backgroundColor: 'var(--besty-blue)',
                    transition: 'width 300ms ease-out',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Body: injected HTML + fade-in transition */}
          <div className="px-6 py-5">
            <style>{`
              .animate-fade-in { animation: fadeIn .18s ease-out }
              @keyframes fadeIn { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: translateY(0) } }
              .agreement-injected > *:first-child { border: none !important; box-shadow: none !important; border-radius: 0 !important; }
              
              /* Restore default HTML styling that might be stripped by template CSS resets */
              .agreement-injected h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
              .agreement-injected h2 { font-size: 1.5em; font-weight: bold; margin: 0.75em 0; }
              .agreement-injected h3 { font-size: 1.17em; font-weight: bold; margin: 0.83em 0; }
              .agreement-injected h4 { font-size: 1em; font-weight: bold; margin: 1.12em 0; }
              .agreement-injected h5 { font-size: 0.83em; font-weight: bold; margin: 1.5em 0; }
              .agreement-injected h6 { font-size: 0.75em; font-weight: bold; margin: 1.67em 0; }
              .agreement-injected strong, .agreement-injected b { font-weight: bold; }
              .agreement-injected em, .agreement-injected i { font-style: italic; }
              .agreement-injected p { margin: 1em 0; }
              .agreement-injected ul, .agreement-injected ol { margin: 1em 0; padding-left: 40px; }
              .agreement-injected ul { list-style-type: disc; }
              .agreement-injected ol { list-style-type: decimal; }
              .agreement-injected li { display: list-item; margin: 0.5em 0; }
              .agreement-injected hr { border: 0; border-top: 1px solid #ccc; margin: 1em 0; }
              .agreement-injected table { border-collapse: collapse; width: 100%; margin: 1em 0; }
              .agreement-injected td, .agreement-injected th { border: 1px solid #ddd; padding: 8px; text-align: left; }
              .agreement-injected th { font-weight: bold; background-color: #f2f2f2; }
              
              /* Style input fields and textareas */
              .agreement-injected input[type="text"],
              .agreement-injected input[type="date"],
              .agreement-injected input[type="email"],
              .agreement-injected input[type="number"],
              .agreement-injected textarea {
                width: 100%;
                padding: 8px 12px;
                border: 1px solid #cbd5e1;
                border-radius: 4px;
                font-size: 14px;
                font-family: inherit;
                line-height: 1.5;
                background-color: white;
                box-sizing: border-box;
              }
              .agreement-injected textarea {
                min-height: 60px;
                resize: vertical;
              }
              .agreement-injected input:focus, .agreement-injected textarea:focus {
                outline: none;
                border-color: var(--besty-blue, #2563eb);
                box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
              }
              
              /* Mobile responsive: stack side-by-side IMAGE elements vertically */
              /* NOTE: Do NOT include [data-signature-id] here - it has its own button layout */
              @media (max-width: 768px) {
                /* Force image blocks to be block-level and full width */
                .agreement-injected [data-selfie-id],
                .agreement-injected [data-id-id],
                .agreement-injected [data-image-id] {
                  display: block !important;
                  width: 100% !important;
                  max-width: 100% !important;
                  flex: none !important;
                  float: none !important;
                  position: static !important;
                  margin: 16px 0 !important;
                  clear: both !important;
                }
                
                /* Force parent containers of IMAGE blocks to stack (not signature!) */
                .agreement-injected div:has(> [data-selfie-id]),
                .agreement-injected div:has(> [data-id-id]),
                .agreement-injected div:has(> [data-image-id]) {
                  display: block !important;
                  height: auto !important;
                  min-height: auto !important;
                }
                
                /* Ensure images don't overflow */
                .agreement-injected [data-selfie-id] img,
                .agreement-injected [data-id-id] img,
                .agreement-injected [data-image-id] img {
                  max-width: 100% !important;
                  height: auto !important;
                  display: block !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                }
              }
            `}</style>
            {(() => {
              const missingPageInfos = getMissingRequiredPages().filter((p) => p.page < currentPageIndex);
              if (missingPageInfos.length === 0 || maxVisitedPageIndex < currentPageIndex) return null;
              return (
                <div
                  className="mb-4 p-3 bg-amber-50 text-amber-900 rounded-lg border-l-4 border-amber-500 animate-fade-in flex items-start gap-3"
                  role="status"
                  aria-live="polite"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-amber-700 mt-0.5 shrink-0"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <div className="flex flex-col gap-2 min-w-0 w-full">
                    <div className="text-sm font-medium">{t('Some required fields on earlier pages still need attention.')}</div>
                    <div className="flex flex-col gap-1.5">
                      {missingPageInfos.map(({ page, fieldLabels }) => (
                        <button
                          key={page}
                          type="button"
                          onClick={async () => {
                            await flushInputSaves();
                            setCurrentPageIndex(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg bg-white border border-amber-200 hover:bg-amber-100 transition-colors"
                        >
                          <span className="text-xs font-semibold text-amber-900 whitespace-nowrap">
                            {t('Page {{page}}', { page: page + 1 })}
                          </span>
                          <span className="text-xs text-amber-700 truncate">
                            {fieldLabels.map(localizeFieldLabel).join(', ')}
                          </span>
                          <span className="ml-auto text-amber-400 shrink-0" aria-hidden="true">
                            →
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
            <div key={currentPageIndex} className="agreement-injected animate-fade-in">
              <div
                dangerouslySetInnerHTML={{
                  __html: `<style>${session.pages[currentPageIndex].css || ''}</style>${(
                    session.pages[currentPageIndex].html || ''
                  ).replace(/contenteditable="[^"]*"/g, '')}`,
                }}
              />
            </div>
          </div>

          {/* Footer: navigation */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
            {currentPageIndex > 0 ? (
              <Button onClick={prevPage} disabled={completing} variant="outline" size="md">
                ← {t('Previous')}
              </Button>
            ) : (
              <div />
            )}

            {currentPageIndex < session.pages.length - 1 ? (
              <Button onClick={nextPage} disabled={completing} variant="primary" size="md">
                {t('Next')} →
              </Button>
            ) : (
              <div />
            )}
          </div>
        </div>
      )}

      {/* Hidden file input for ID document upload */}
      <input
        ref={idInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        disabled={isCompletedSession}
        onChange={onIdPicked}
      />

      {/* Hidden file inputs for custom image uploads */}
      {session?.imageUploads?.map((imageUpload) => (
        <input
          key={imageUpload.id}
          ref={(el) => {
            customImageInputRefs.current[imageUpload.id] = el;
          }}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          disabled={isCompletedSession}
          onChange={onImagePicked(imageUpload.id)}
        />
      ))}

      {/* Selfie Capture Modal */}
      {selfieModalOpen && selfieCaptureToken && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => {
            setSelfieModalOpen(false);
            setSelfieCaptureToken(null);
            pendingGuestSelfieRef.current = null;
          }}
        >
          <div
            style={{
              width: 'min(640px, 96vw)',
              height: 'min(760px, 92vh)',
              background: '#fff',
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(0,0,0,.35)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setSelfieModalOpen(false);
                setSelfieCaptureToken(null);
                pendingGuestSelfieRef.current = null;
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                border: '1px solid #e5e7eb',
                background: '#fff',
                borderRadius: 6,
                padding: '4px 8px',
                cursor: 'pointer',
                zIndex: 2,
              }}
              aria-label={t('Close')}
            >
              ✕
            </button>
            <iframe
              title={t('Selfie Capture')}
              src={`/selfie-capture?token=${encodeURIComponent(selfieCaptureToken)}`}
              style={{ width: '100%', height: '100%', border: 0 }}
              allow="camera; microphone"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
      )}

      {/* Initials Input Modal */}
      {initialsModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => {
            setInitialsModalOpen(false);
            setInitialsModalId(null);
            setInitialsModalValue('');
          }}
        >
          <div
            style={{
              width: 'min(380px, 90vw)',
              background: '#fff',
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(0,0,0,.35)',
              position: 'relative',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setInitialsModalOpen(false);
                setInitialsModalId(null);
                setInitialsModalValue('');
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                border: '1px solid #e5e7eb',
                background: '#fff',
                borderRadius: 6,
                padding: '4px 8px',
                cursor: 'pointer',
                zIndex: 2,
              }}
              aria-label={t('Close')}
            >
              ✕
            </button>

            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>
                {t('Enter Your Initials')}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{t('Type your initials below (e.g., J.D.)')}</div>
            </div>

            <input
              autoFocus
              type="text"
              value={initialsModalValue}
              onChange={(e) => setInitialsModalValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && initialsModalValue.trim()) {
                  submitInitials();
                }
              }}
              placeholder="J.D."
              maxLength={10}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: 22,
                fontWeight: 700,
                fontFamily: "'Times New Roman', Georgia, serif",
                fontStyle: 'italic',
                textAlign: 'center',
                letterSpacing: 2,
                border: '2px solid #e5e7eb',
                borderRadius: 10,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--besty-blue, #2563eb)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />

            {initialsModalValue.trim() && (
              <div
                style={{
                  marginTop: 12,
                  padding: '10px 16px',
                  background: '#f8fafc',
                  borderRadius: 8,
                  textAlign: 'center',
                }}
              >
                <span style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>
                  {t('Preview')}
                </span>
                <span
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    fontFamily: "'Times New Roman', Georgia, serif",
                    fontStyle: 'italic',
                    color: '#1a1a1a',
                    letterSpacing: 2,
                  }}
                >
                  {initialsModalValue.trim().toUpperCase()}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => {
                  setInitialsModalOpen(false);
                  setInitialsModalId(null);
                  setInitialsModalValue('');
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#374151',
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                {t('Cancel')}
              </button>
              <button
                onClick={submitInitials}
                disabled={!initialsModalValue.trim()}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  background: initialsModalValue.trim()
                    ? 'var(--besty-blue, #2563eb)'
                    : 'var(--besty-blue-disabled, #93c5fd)',
                  border: 'none',
                  borderRadius: 8,
                  cursor: initialsModalValue.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t('Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signer-only CSS overrides - separate from page content to prevent malformed injection */}
      <style>{`
/* Signer-only: normalize template variable "chips" to plain inline text */
[data-runtime="signer"] .tv-chip,
[data-runtime="signer"] .template-variable,
[data-runtime="signer"] .variable-chip,
[data-runtime="signer"] [data-template-var],
[data-runtime="signer"] [data-template-variable],
[data-runtime="signer"] [data-besty-var],
[data-runtime="signer"] span[data-var] {
  display: inline !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
  color: inherit !important;
  font: inherit !important;
  line-height: inherit !important;
  letter-spacing: inherit !important;
  text-transform: inherit !important;
  vertical-align: baseline !important;
  white-space: normal !important;
  outline: none !important;
}

/* Remove any decorative pseudo content (badges, brackets) */
[data-runtime="signer"] .tv-chip::before,
[data-runtime="signer"] .tv-chip::after,
[data-runtime="signer"] .template-variable::before,
[data-runtime="signer"] .template-variable::after,
[data-runtime="signer"] [data-template-var]::before,
[data-runtime="signer"] [data-template-var]::after {
  content: none !important;
}

/* Hide legacy default input headers without touching author-provided headings */
[data-runtime="signer"] .default-input-header {
  display: none !important;
}

/* Signer only: disable resizing but keep layout */
[data-runtime="signer"] .ra-input { 
  resize: none; 
}
[data-runtime="signer"] .ra-input > input {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}
      `}</style>
    </Layout>
  );
};

export default SignAgreement;
